/**
 * RedlineInserter - STAGE 3: PRESENTATION
 * 
 * Handles insertion of content with track changes into Word documents.
 * 
 * Key feature: insertAsTrackedChangeWithSearch now supports OOXML insertion
 * - When editedOoxml is provided, tries insertOoxml first (word-level track changes)
 * - Falls back to insertText if OOXML insertion fails
 * - Uses OoxmlPackageWrapper to wrap paragraph OOXML in package format
 * - Preserves w:ins and w:del elements so Word displays native track changes
 */

import { TrackChangesManager } from './TrackChangesManager';
import { DocumentReader, ParagraphApplyResolveOptions } from '../api/DocumentReader';
import { TrackChangesParser } from '../utils/TrackChangesParser';
import { OoxmlPackageWrapper } from '../utils/OoxmlPackageWrapper';
import { computeWordDiff } from '../utils/TextDiff';
import { log, logError, logWarn } from '../../utils/logging';
import { prepareBidiTextForWordInsert } from '../../utils/bidiText';
import { logQuickResponseStage } from '../../utils/quickResponseDebug';

/* global Word */

/** Word.js Range.search rejects strings longer than this (RichApi: SearchStringInvalidOrTooLong). */
export const WORD_JS_MAX_SEARCH_STRING_LENGTH = 255;

/** Minimum delete/search anchor length — shorter needles (e.g. ".") match inside "3.5". */
const MIN_DELETE_SEARCH_ANCHOR_LEN = 4;

type ParsedHtmlOp =
  | { kind: 'delete'; text: string; charPos: number }
  | { kind: 'replace'; deleteText: string; insertText: string; charPos: number }
  | { kind: 'insert'; text: string; charPos: number; suffixWords: string[] };

function normalizeWs(t: string): string {
  return (t || '').replace(/\s+/g, ' ').trim();
}

/** Max padding when matching Word tracked-change dates to a submit apply window. */
export const SUBMIT_REVERT_TOLERANCE_MS = 1000;

export type TrackedChangeFingerprint = {
  dateIso: string;
  type: string;
};

/** Captured at submit apply time (ms precision) for precise undo. */
export type SubmitRevertContext = {
  applyStartedAtMs: number;
  applyFinishedAtMs: number;
  addedFingerprints: TrackedChangeFingerprint[];
  /** Resolved Word body paragraph index used for apply (may differ from stale clause hint). */
  paragraphIndex: number;
  /**
   * Original paragraph text BEFORE apply — captured on the insertText path where fingerprints
   * are unavailable (Win32). Used by the Win32 undo path to restore the paragraph via
   * TC-off + insertOoxml/insertText when tc.reject() is not functional.
   */
  originalParagraphText?: string;
  /**
   * Original paragraph XML (w:p element, not the full package) BEFORE apply — captured on the
   * insertText path. Enables the Win32 undo path to restore pre-existing tracked changes
   * (e.g. counterparty edits) that would be lost if only plain text were re-inserted.
   * Only the paragraph XML is stored (not the 100KB+ full package) to keep state lean.
   */
  originalParagraphOoxml?: string;
  /**
   * Original pkg:package XML returned by `paragraph.getOoxml()` BEFORE apply, with the body
   * surgically trimmed to contain ONLY the target <w:p>. Preserves Word's full package shape
   * (namespaces, package parts, attribute ordering) so `Range.insertOoxml(...)` reliably
   * restores the paragraph on Win32 — including pre-existing tracked changes, paragraph
   * style references, numbering, and run properties — without manually reconstructing OOXML.
   *
   * This is the preferred undo input on Win32. The minimal hand-crafted package produced by
   * `OoxmlPackageWrapper.wrapParagraphInPackage` is rejected by Win32 `insertOoxml` with
   * `GeneralException` for many real paragraphs because it lacks namespace declarations /
   * package parts that Win32 requires; round-tripping Word's own output avoids that.
   *
   * For table cells this is a lifted `<w:p>` package (no wrapping `w:tbl`). insertOoxml of that
   * paragraph restores pre-existing `w:ins`/`w:del`. Never insert a raw cell getOoxml() table.
   */
  originalParagraphOoxmlPackage?: string;
  /**
   * Paragraph lived in a table cell. Undo must restore via a table-free paragraph OOXML package
   * (or fingerprint `tc.reject` of this apply only). Never TC-off insertText — that flattens
   * counterparty track changes into clean text.
   */
  inTable?: boolean;
  /**
   * Restore the original paragraph snapshot DIRECTLY (via {@link revertViaTextReplacement}) instead
   * of the fingerprint/window `tc.reject()` pass. Set by the hybrid engine: its native edits are
   * stored by Word as many tracked-change segments that (date+type)+window matching reverts only
   * partially. A deterministic snapshot restore puts the paragraph back exactly (counterparty
   * revisions preserved) with no guesswork. Requires an original snapshot to be present.
   */
  preferOriginalRestore?: boolean;
  /**
   * Set by the server when it omitted `originalParagraphOoxmlPackage` from a clause list
   * (packages are ~300 KB each). Undo fetches the full context before reverting.
   */
  hasOriginalParagraphOoxmlPackage?: boolean;
};

export type ApplyParagraphResult = {
  applied: boolean;
  revertContext?: SubmitRevertContext;
};

function trackedChangeFingerprintKey(f: TrackedChangeFingerprint): string {
  return `${f.dateIso}|${f.type}`;
}

function revertSnapshotIsTableCell(revertContext: SubmitRevertContext | null | undefined): boolean {
  if (!revertContext) return false;
  if (revertContext.inTable === true) return true;
  return OoxmlPackageWrapper.containsTableMarkup(
    revertContext.originalParagraphOoxmlPackage || revertContext.originalParagraphOoxml || '',
  );
}

async function liveParagraphIsInTable(
  paragraph: {
    parentTable?: { load: (s: string) => void };
    parentTableOrNullObject?: { isNullObject?: boolean; load: (s: string) => void };
  },
  context: Word.RequestContext,
): Promise<boolean> {
  try {
    if (paragraph.parentTableOrNullObject) {
      paragraph.parentTableOrNullObject.load('isNullObject');
      await context.sync();
      return paragraph.parentTableOrNullObject.isNullObject === false;
    }
  } catch {
    // Older Word builds may not expose parentTableOrNullObject.
  }
  try {
    if (!paragraph.parentTable) return false;
    paragraph.parentTable.load('rowCount');
    await context.sync();
    return true;
  } catch {
    return false;
  }
}

function diffAddedTrackedChangeFingerprints(
  before: TrackedChangeFingerprint[],
  after: TrackedChangeFingerprint[],
): TrackedChangeFingerprint[] {
  const counts = new Map<string, number>();
  for (const f of before) {
    const k = trackedChangeFingerprintKey(f);
    counts.set(k, (counts.get(k) ?? 0) + 1);
  }
  const added: TrackedChangeFingerprint[] = [];
  for (const f of after) {
    const k = trackedChangeFingerprintKey(f);
    const remaining = counts.get(k) ?? 0;
    if (remaining > 0) {
      counts.set(k, remaining - 1);
    } else {
      added.push(f);
    }
  }
  return added;
}

function filterFingerprintsToSubmitApply(
  added: TrackedChangeFingerprint[],
  applyStartedAtMs: number,
  applyFinishedAtMs: number,
): TrackedChangeFingerprint[] {
  // Word stamps tc dates at minute precision; allow one minute slack when filtering
  // snapshot noise (e.g. counterparty TCs from 2025 re-listed after structural edits).
  const windowStart = applyStartedAtMs - 60_000;
  const windowEnd = applyFinishedAtMs + 60_000;
  return added.filter((f) => {
    const ms = Date.parse(f.dateIso);
    return Number.isFinite(ms) && ms >= windowStart && ms <= windowEnd;
  });
}

function buildSubmitRevertContext(
  applyStartedAtMs: number,
  applyFinishedAtMs: number,
  before: TrackedChangeFingerprint[],
  after: TrackedChangeFingerprint[],
  paragraphIndex: number,
): SubmitRevertContext {
  const rawAdded = diffAddedTrackedChangeFingerprints(before, after);
  return {
    applyStartedAtMs,
    applyFinishedAtMs,
    addedFingerprints: filterFingerprintsToSubmitApply(
      rawAdded,
      applyStartedAtMs,
      applyFinishedAtMs,
    ),
    paragraphIndex,
  };
}

function isTrackedChangeInApplyWindow(
  tcMs: number,
  ctx: SubmitRevertContext,
  toleranceMs: number,
): boolean {
  const windowStart = ctx.applyStartedAtMs - toleranceMs;
  const windowEnd = ctx.applyFinishedAtMs + toleranceMs;
  if (tcMs >= windowStart && tcMs <= windowEnd) {
    return true;
  }
  // Word stamps tracked-change dates at minute precision (:00.000Z).
  const applyMinuteStart = Math.floor(ctx.applyStartedAtMs / 60_000) * 60_000;
  const tcMinuteStart = Math.floor(tcMs / 60_000) * 60_000;
  return tcMinuteStart === applyMinuteStart;
}

/** Wider window when fingerprints were unavailable at submit time (common on Word Win32). */
function revertMatchToleranceMs(
  ctx: SubmitRevertContext,
  toleranceMs: number,
): number {
  if (ctx.addedFingerprints.length === 0) {
    return Math.max(toleranceMs, 60_000);
  }
  return toleranceMs;
}

/** C0 controls Word paragraph.text may include; Range.search cannot match them (e.g. \\u0005). */
function isWordSearchNoiseChar(c: string): boolean {
  const code = c.charCodeAt(0);
  return (
    (code >= 0x00 && code <= 0x08) ||
    code === 0x0b ||
    code === 0x0c ||
    (code >= 0x0e && code <= 0x1f) ||
    code === 0x7f
  );
}

function stripWordSearchNoise(text: string): string {
  let out = '';
  for (const c of text) {
    if (!isWordSearchNoiseChar(c)) out += c;
  }
  return out;
}

type StrippedTextMap = { cleaned: string; indexMap: number[] };

function stripWordSearchNoiseWithMap(text: string): StrippedTextMap {
  const cleaned: string[] = [];
  const indexMap: number[] = [];
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (isWordSearchNoiseChar(c)) continue;
    cleaned.push(c);
    indexMap.push(i);
  }
  return { cleaned: cleaned.join(''), indexMap };
}

function mapCleanedPosToOriginal(indexMap: number[], cleanPos: number): number {
  if (cleanPos <= 0) return indexMap[0] ?? 0;
  if (cleanPos >= indexMap.length) {
    return (indexMap[indexMap.length - 1] ?? 0) + 1;
  }
  return indexMap[cleanPos];
}

/** Collapse punctuation/whitespace differences between OOXML accepted and Word API text. */
function collapseAnchorPunctuation(text: string): string {
  return stripWordSearchNoise(text)
    .replace(/\.\s+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** All non-overlapping start indices of `needle` in `haystack`. */
function findAllOccurrenceIndices(haystack: string, needle: string): number[] {
  if (!needle) return [];
  const indices: number[] = [];
  let pos = 0;
  const hay = haystack.toLowerCase();
  const n = needle.toLowerCase();
  while (pos < hay.length) {
    const idx = hay.indexOf(n, pos);
    if (idx === -1) break;
    indices.push(idx);
    pos = idx + 1;
  }
  return indices;
}

/** Context windows around char positions for baseline/search debugging. */
function textContextAtPositions(
  text: string,
  positions: number[],
  window = 40,
): Array<{ pos: number; context: string; charAtPos: string }> {
  return positions.map((pos) => ({
    pos,
    context: text.slice(Math.max(0, pos - window), Math.min(text.length, pos + window)),
    charAtPos: pos < text.length ? text[pos] : '(eof)',
  }));
}

/** First index where two strings differ (for mismatch logs). */
function firstTextMismatchInfo(
  a: string,
  b: string,
  contextChars = 48,
): { index: number; sliceA: string; sliceB: string } | null {
  const max = Math.max(a.length, b.length);
  for (let i = 0; i < max; i++) {
    const ca = i < a.length ? a[i] : '';
    const cb = i < b.length ? b[i] : '';
    if (ca !== cb) {
      const start = Math.max(0, i - contextChars);
      return {
        index: i,
        sliceA: a.slice(start, i + contextChars),
        sliceB: b.slice(start, i + contextChars),
      };
    }
  }
  return null;
}

/** Minimum snippet length before we trust body-wide "contains" matching (avoids dollar amounts etc.). */
const MIN_SNIPPET_LEN_FOR_CONTAINS = 24;

export class RedlineInserter {
  private static escapeHtmlForWordDiff(text: string): string {
    return (text || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Same shape as ProofreadWordAdapter.buildTrackChangesHtml — used when the caller's
   * originalText (e.g. stale client_request) does not match Word's live paragraph but
   * we still have the correct paragraph by index.
   */
  private static buildTrackChangesHtmlFromWordDiff(original: string, modified: string): string {
    const ops = computeWordDiff(original, modified);
    let html = '';
    for (let i = 0; i < ops.length; i++) {
      const op = ops[i];
      const chunk = RedlineInserter.escapeHtmlForWordDiff(op.words.join(' '));
      const needsTrailingSpace = i < ops.length - 1;
      const spaceSuffix = needsTrailingSpace ? ' ' : '';
      if (op.type === 'equal') {
        html += chunk + spaceSuffix;
      } else if (op.type === 'delete') {
        html += `<del class="track-change-deletion track-change-new">${chunk}</del>${spaceSuffix}`;
      } else {
        html += `<ins class="track-change-insertion track-change-new">${chunk}</ins>${spaceSuffix}`;
      }
    }
    return html;
  }

  /**
   * Inserts text as a tracked change, replacing the current selection
   * This works best on Windows Desktop where Track Changes is fully supported
   */
  static async insertAsTrackedChange(text: string): Promise<void> {
    try {
      // RTL-only: keep [](){} from glyph-mirroring in Word (no-op for English).
      const insertText = prepareBidiTextForWordInsert(text);
      // Ensure Track Changes is enabled
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      
      if (!trackChangesEnabled) {
        log('Track Changes not available, inserting as regular text');
        // Fallback to regular insertion
        await this.insertAsRegularText(insertText);
        return;
      }

      await Word.run(async (context) => {
        const selection = context.document.getSelection();
        
        // Load selection properties
        selection.load('text');
        await context.sync();
        
        // Only replace if there's text selected, otherwise insert at cursor
        if (selection.text && selection.text.trim().length > 0) {
          // Replace the selection with the new text
          selection.insertText(insertText, Word.InsertLocation.replace);
        } else {
          // If no selection, insert at cursor position
          selection.insertText(insertText, Word.InsertLocation.before);
        }
        
        await context.sync();
        log('Text inserted as tracked change (redline)');
      });
    } catch (error) {
      logError('Failed to insert as tracked change', error);
      // Fallback to regular insertion
      await this.insertAsRegularText(prepareBidiTextForWordInsert(text));
    }
  }

  /**
   * Inserts text as a tracked change in a specific range
   * Note: Range objects must be obtained within the same Word.run() context.
   * This method is deprecated - use insertAsTrackedChange() instead which uses current selection.
   */
  static async insertAsTrackedChangeInRange(range: Word.Range, text: string): Promise<void> {
    try {
      const insertText = prepareBidiTextForWordInsert(text);
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      
      if (!trackChangesEnabled) {
        log('Track Changes not available, inserting as regular text');
        await this.insertAsRegularTextInRange(range, insertText);
        return;
      }

      await Word.run(async (context) => {
        // Use the range directly - it must be from the same context
        range.insertText(insertText, Word.InsertLocation.replace);
        await context.sync();
        log('Text inserted as tracked change in range');
      });
    } catch (error) {
      logError('Failed to insert as tracked change in range', error);
      // Fallback: use current selection instead
      await this.insertAsTrackedChange(text);
    }
  }

  /**
   * Fallback: Insert as regular text (no tracking)
   */
  private static async insertAsRegularText(text: string): Promise<void> {
    await Word.run(async (context) => {
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      
      if (selection.text && selection.text.trim().length > 0) {
        selection.insertText(text, Word.InsertLocation.replace);
      } else {
        selection.insertText(text, Word.InsertLocation.before);
      }
      await context.sync();
    });
  }

  /**
   * Public fallback: replace text by finding it in the document (search or current selection).
   * Use when applyWordLevelChangesIncrementally fails (e.g. paragraph not found).
   * No track changes; replaces matching selection or first document match.
   */
  static async replaceBySearchOrSelection(
    originalText: string,
    newText: string,
    reason?: string,
  ): Promise<void> {
    log('[RedlineInserter] replaceBySearchOrSelection: start', {
      reason: reason ?? 'unspecified',
      originalLen: originalText?.length ?? 0,
      newLen: newText?.length ?? 0,
      note: 'fallback path — whole selection/body replace, not incremental track changes',
    });
    await this.insertAsRegularTextWithSearch(originalText, newText);
  }

  /**
   * Fallback: Search and replace as regular text (no tracking)
   * Optimized: First tries current selection (fast), then falls back to full document search (slow)
   */
  private static async insertAsRegularTextWithSearch(originalText: string, newText: string): Promise<void> {
    const insertText = prepareBidiTextForWordInsert(newText);
    await Word.run(async (context) => {
      // OPTIMIZATION: First try current selection (fast - no search needed)
      const selection = context.document.getSelection();
      selection.load('text');
      await context.sync();
      
      const selectionText = selection.text ? selection.text.trim() : '';
      const originalTextTrimmed = originalText.trim();
      
      // Check if current selection matches the original text (normalized comparison)
      const normalizeText = (text: string) => text.replace(/\s+/g, ' ').trim();
      const selectionNormalized = normalizeText(selectionText);
      const originalNormalized = normalizeText(originalTextTrimmed);
      
      if (selectionNormalized === originalNormalized || 
          selectionText === originalTextTrimmed ||
          selectionText.includes(originalTextTrimmed) ||
          originalTextTrimmed.includes(selectionText)) {
        // Current selection matches - use it directly (FAST PATH)
        log('[RedlineInserter] replaceBySearchOrSelection: selection fast path (no tracking)', {
          selectionLen: selectionText.length,
          originalLen: originalTextTrimmed.length,
          newLen: insertText.length,
        });
        
        if (selection.text && selection.text.trim().length > 0) {
          selection.insertText(insertText, Word.InsertLocation.replace);
        } else {
          selection.insertText(insertText, Word.InsertLocation.before);
        }
        await context.sync();
        return;
      }
      
      // FALLBACK: Selection doesn't match - search entire document (SLOW PATH)
      log('[RedlineInserter] replaceBySearchOrSelection: document search (no tracking)', {
        selectionLen: selectionText.length,
        originalLen: originalTextTrimmed.length,
      });
      const searchResults = context.document.body.search(originalTextTrimmed, {
        matchCase: false,
        matchWholeWord: false,
      });
      
      searchResults.load('items');
      await context.sync();

      if (searchResults.items.length > 0) {
        const foundRange = searchResults.items[0];
        foundRange.insertText(insertText, Word.InsertLocation.replace);
        await context.sync();
      } else {
        // Last resort: use current selection even if it doesn't match
        log('[RedlineInserter] replaceBySearchOrSelection: search miss, selection fallback (no tracking)', {
          searchResultCount: searchResults.items.length,
        });
        if (selection.text && selection.text.trim().length > 0) {
          selection.insertText(insertText, Word.InsertLocation.replace);
        } else {
          selection.insertText(insertText, Word.InsertLocation.before);
        }
        await context.sync();
      }
    });
  }

  /**
   * Fallback: Insert as regular text in range
   * Note: Range objects must be obtained within the same Word.run() context
   */
  private static async insertAsRegularTextInRange(range: Word.Range, text: string): Promise<void> {
    await Word.run(async (context) => {
      // Use the range directly - it must be from the same context
      range.insertText(text, Word.InsertLocation.replace);
      await context.sync();
    });
  }

  /* ================================================================
   *  NEW: Incremental word-level track changes
   *
   *  1. Find the paragraph by matching its text against all paragraphs
   *  2. Select the entire paragraph
   *  3. Compute word-level diff (LCS) → group into ChangeChunks
   *  4. For each chunk (left→right):
   *       • replace / delete  → search for oldText inside the paragraph range,
   *                              disambiguate with prefix context when needed
   *       • insert            → search for the suffix word (or prefix),
   *                              insert the new words before / after it
   *     Each individual Word API operation (insertText / delete) is tracked
   *     separately by Word's Track Changes → word-level redlines.
   * ================================================================ */

  /** Derive the search anchor string used by incremental apply for a parsed op. */
  private static searchTextForOp(op: ParsedHtmlOp): string {
    if (op.kind === 'replace') return op.deleteText;
    if (op.kind === 'delete') return op.text;
    if (op.kind === 'insert' && op.suffixWords.length > 0) return op.suffixWords[0];
    return '';
  }

  /**
   * When OOXML accepted text diverges from Word visible text, map an op's delete span from
   * ooxml charPos to the corresponding span in apiText using prefix+suffix anchoring.
   * Handles counterparty w:ins boundaries (extra text + \\u0005 field markers in API).
   */
  private static locateDeleteSpanInApi(
    ooxmlBaseline: string,
    apiText: string,
    charPos: number,
    deleteText: string,
  ): { effectiveCharPos: number; effectiveDeleteText: string } | null {
    const PREFIX_LEN = 40;
    const SUFFIX_LEN = 25;

    const prefix = ooxmlBaseline.slice(Math.max(0, charPos - PREFIX_LEN), charPos);
    const suffix = ooxmlBaseline.slice(
      charPos + deleteText.length,
      charPos + deleteText.length + SUFFIX_LEN,
    );

    const prefixClean = stripWordSearchNoise(prefix);
    const suffixClean = stripWordSearchNoise(suffix);
    if (!prefixClean) return null;

    const ooxmlClean = stripWordSearchNoise(ooxmlBaseline);
    const { cleaned: apiClean, indexMap } = stripWordSearchNoiseWithMap(apiText);

    const prefixOccIdx = RedlineInserter.countOccurrencesBefore(
      ooxmlClean,
      prefixClean,
      stripWordSearchNoise(ooxmlBaseline.slice(0, charPos)).length,
    );

    if (!deleteText) {
      let occ = 0;
      let searchFrom = 0;
      while (searchFrom < apiClean.length) {
        const idx = apiClean.indexOf(prefixClean, searchFrom);
        if (idx === -1) break;
        if (occ === prefixOccIdx) {
          const effectiveCharPos = mapCleanedPosToOriginal(indexMap, idx + prefixClean.length);
          return { effectiveCharPos, effectiveDeleteText: '' };
        }
        occ++;
        searchFrom = idx + 1;
      }
      return null;
    }

    let occ = 0;
    let searchFrom = 0;
    while (searchFrom < apiClean.length) {
      const idx = apiClean.indexOf(prefixClean, searchFrom);
      if (idx === -1) break;
      const candidateStartClean = idx + prefixClean.length;
      if (suffixClean) {
        const suffixNeedle =
          apiClean.indexOf(suffixClean, candidateStartClean) !== -1
            ? suffixClean
            : suffixClean.replace(/^\s+/, '');
        const sufIdx = apiClean.indexOf(suffixNeedle, candidateStartClean);
        if (sufIdx !== -1) {
          if (occ === prefixOccIdx) {
            const effectiveCharPos = mapCleanedPosToOriginal(indexMap, candidateStartClean);
            const effectiveEnd = mapCleanedPosToOriginal(indexMap, sufIdx);
            return {
              effectiveCharPos,
              effectiveDeleteText: apiText.slice(effectiveCharPos, effectiveEnd),
            };
          }
          occ++;
        }
      } else if (occ === prefixOccIdx) {
        const effectiveCharPos = mapCleanedPosToOriginal(indexMap, candidateStartClean);
        const cleanEnd = Math.min(
          candidateStartClean + stripWordSearchNoise(deleteText).length,
          apiClean.length,
        );
        const effectiveEnd = mapCleanedPosToOriginal(indexMap, cleanEnd);
        return {
          effectiveCharPos,
          effectiveDeleteText: apiText.slice(effectiveCharPos, effectiveEnd),
        };
      }
      searchFrom = idx + 1;
    }

    return RedlineInserter.locateDeleteSpanSuffixFirst(
      ooxmlBaseline,
      apiText,
      charPos,
      deleteText,
      prefixClean,
      suffixClean,
    );
  }

  /**
   * Suffix-first anchoring when prefix-forward matching fails (early REF/field skew like
   * "6.2" vs "6.2‏6.3", or API punctuation "היזם." vs OOXML "היזם").
   */
  private static locateDeleteSpanSuffixFirst(
    ooxmlBaseline: string,
    apiText: string,
    charPos: number,
    deleteText: string,
    prefixClean: string,
    suffixClean: string,
  ): { effectiveCharPos: number; effectiveDeleteText: string } | null {
    if (!deleteText || !suffixClean) return null;

    const ooxmlClean = stripWordSearchNoise(ooxmlBaseline);
    const { cleaned: apiClean, indexMap } = stripWordSearchNoiseWithMap(apiText);

    const suffixVariants = [
      suffixClean,
      suffixClean.replace(/^\s+/, ''),
      suffixClean.replace(/^\s*\.\s*/, ''),
    ].filter((s, i, arr) => s.length >= 3 && arr.indexOf(s) === i);

    let suffixIndices: number[] = [];
    for (const variant of suffixVariants) {
      suffixIndices = findAllOccurrenceIndices(apiClean, variant);
      if (suffixIndices.length > 0) break;
    }
    if (suffixIndices.length === 0) return null;

    const suffixStartInOoxmlClean = stripWordSearchNoise(
      ooxmlBaseline.slice(0, charPos + deleteText.length),
    ).length;
    const suffixOccIdx = RedlineInserter.countOccurrencesBefore(
      ooxmlClean,
      suffixClean,
      suffixStartInOoxmlClean,
    );

    const deleteClean = collapseAnchorPunctuation(deleteText);
    const prefixTails: string[] = [];
    if (prefixClean.length >= 4) prefixTails.push(prefixClean);
    for (let len = Math.min(35, prefixClean.length); len >= 6; len--) {
      const tail = prefixClean.slice(-len);
      if (!prefixTails.includes(tail)) prefixTails.push(tail);
    }
    if (deleteClean.length >= 6) prefixTails.push(deleteClean);

    const suffixCandidates = [
      suffixOccIdx < suffixIndices.length ? suffixIndices[suffixOccIdx] : null,
      ...suffixIndices,
    ].filter((v, i, arr): v is number => v != null && arr.indexOf(v) === i);

    for (const sufIdx of suffixCandidates) {
      const beforeSuffix = apiClean.slice(0, sufIdx);
      for (const tail of prefixTails) {
        const anchorPos = beforeSuffix.lastIndexOf(tail);
        if (anchorPos === -1) continue;
        const candidateStartClean = anchorPos + tail.length;
        const effectiveCharPos = mapCleanedPosToOriginal(indexMap, candidateStartClean);
        const effectiveEnd = mapCleanedPosToOriginal(indexMap, sufIdx);
        const span = apiText.slice(effectiveCharPos, effectiveEnd);
        if (!span) continue;

        const spanCollapsed = collapseAnchorPunctuation(span);
        const lenDelta = Math.abs(span.length - deleteText.length);
        const lenOk =
          span.length >= Math.max(1, deleteText.length - 10) &&
          span.length <= deleteText.length + 20;
        const contentOk =
          spanCollapsed === deleteClean ||
          lenDelta <= 6 ||
          (deleteClean.length >= 10 &&
            (spanCollapsed.includes(deleteClean) ||
              Math.abs(spanCollapsed.length - deleteClean.length) <= 10));
        if (lenOk && contentOk) {
          return { effectiveCharPos, effectiveDeleteText: span };
        }
      }
    }

    return null;
  }

  /** Remap preview-HTML ops (OOXML char positions) onto Word visible paragraph text. */
  private static remapOpsToApiBaseline(
    ops: ParsedHtmlOp[],
    ooxmlBaseline: string,
    apiText: string,
  ): ParsedHtmlOp[] {
    return ops.map((op) => {
      if (op.kind === 'delete') {
        const located = RedlineInserter.locateDeleteSpanInApi(
          ooxmlBaseline,
          apiText,
          op.charPos,
          op.text,
        );
        if (!located) {
          logWarn('[RedlineInserter] remapOpsToApiBaseline: locate failed (delete)', {
            charPos: op.charPos,
            deleteSnippet: op.text.slice(0, 40),
          });
          return op;
        }
        return {
          ...op,
          charPos: located.effectiveCharPos,
          text: located.effectiveDeleteText,
        };
      }
      if (op.kind === 'replace') {
        const located = RedlineInserter.locateDeleteSpanInApi(
          ooxmlBaseline,
          apiText,
          op.charPos,
          op.deleteText,
        );
        if (!located) {
          logWarn('[RedlineInserter] remapOpsToApiBaseline: locate failed (replace)', {
            charPos: op.charPos,
            deleteSnippet: op.deleteText.slice(0, 40),
            insertSnippet: op.insertText.slice(0, 40),
          });
          return op;
        }
        return {
          ...op,
          charPos: located.effectiveCharPos,
          deleteText: located.effectiveDeleteText,
        };
      }
      if (op.kind === 'insert') {
        const located = RedlineInserter.locateDeleteSpanInApi(
          ooxmlBaseline,
          apiText,
          op.charPos,
          '',
        );
        if (!located) return op;
        return { ...op, charPos: located.effectiveCharPos };
      }
      return op;
    });
  }

  private static countOccurrencesIn(fullText: string, needle: string): number {
    if (!needle) return 0;
    const hay = fullText.toLowerCase();
    const n = needle.toLowerCase();
    let count = 0;
    let pos = 0;
    while (pos < hay.length) {
      const idx = hay.indexOf(n, pos);
      if (idx === -1) break;
      count++;
      pos = idx + 1;
    }
    return count;
  }

  /** Use accepted-text bytes at charPos so spacing matches the preview baseline. */
  private static resolveDeleteSearchText(
    actualParaText: string,
    charPos: number,
    deleteText: string,
  ): string {
    if (!deleteText) return '';
    const end = Math.min(actualParaText.length, charPos + deleteText.length);
    const fromBaseline = actualParaText.slice(charPos, end);
    const slice = fromBaseline.length >= deleteText.length ? fromBaseline : deleteText;

    if (
      slice.length >= MIN_DELETE_SEARCH_ANCHOR_LEN &&
      RedlineInserter.countOccurrencesIn(actualParaText, slice) === 1
    ) {
      return slice;
    }

    const MIN = 8;
    const MAX = 120;
    let start = charPos;
    let endPos = charPos + slice.length;
    while (endPos - start < MIN && start > 0) start--;
    let candidate = actualParaText.slice(start, endPos);
    while (
      RedlineInserter.countOccurrencesIn(actualParaText, candidate) > 1 &&
      start > 0 &&
      candidate.length < MAX
    ) {
      start--;
      candidate = actualParaText.slice(start, endPos);
    }
    return candidate;
  }

  /**
   * Locate the best search anchor for a `replace` op.
   *
   * The old behavior — `actualParaText.slice(charPos, charPos + deleteText.length)` — breaks when
   * coalesceParsedOps merges adjacent ops near counterparty `w:ins`/`w:del` markup and shifts
   * `charPos` by 1–2 chars. The slice then returns a garbled string like "ת היז" that does not
   * exist in Word's text and `Range.search` returns 0 matches.
   *
   * Strategy (in order of preference):
   *   1. Exact slice at charPos already equals `deleteText` → trust the position.
   *   2. `deleteText` appears in a small window centered on charPos → relocate to that offset.
   *   3. Trimmed (no leading/trailing whitespace) `deleteText` appears in the same window.
   *   4. Fall back to the slice (or `deleteText` if slice is empty) — preserves prior behavior.
   *
   * Returns the chosen anchor and an `effectiveCharPos` so `countOccurrencesBefore` can pick the
   * correct match when multiple identical occurrences exist in the paragraph.
   */
  private static resolveReplaceSearchText(
    actualParaText: string,
    charPos: number,
    deleteText: string,
  ): { searchText: string; effectiveCharPos: number } {
    if (!deleteText) {
      return { searchText: '', effectiveCharPos: charPos };
    }

    const exactSlice = actualParaText.slice(charPos, charPos + deleteText.length);
    if (exactSlice === deleteText) {
      log('[RedlineInserter] resolveReplaceSearchText: exact slice match', {
        charPos,
        deleteTextSnippet: deleteText.slice(0, 40),
      });
      return { searchText: deleteText, effectiveCharPos: charPos };
    }

    // Small window centered on charPos. Coalesce-induced drift is typically ±2 chars; we extend
    // a bit further for safety, but stay tight so we don't relocate to a wholly different region.
    const driftBudget = Math.max(8, Math.ceil(deleteText.length / 2));
    const windowStart = Math.max(0, charPos - driftBudget);
    const windowEnd = Math.min(actualParaText.length, charPos + deleteText.length + driftBudget);
    const window = actualParaText.slice(windowStart, windowEnd);

    const localIdx = window.indexOf(deleteText);
    if (localIdx !== -1) {
      const effectiveCharPos = windowStart + localIdx;
      log('[RedlineInserter] resolveReplaceSearchText: relocated in window', {
        charPos,
        effectiveCharPos,
        drift: effectiveCharPos - charPos,
        deleteTextSnippet: deleteText.slice(0, 40),
        windowSnippet: window.slice(0, 60),
      });
      return { searchText: deleteText, effectiveCharPos };
    }

    const trimmed = deleteText.trim();
    if (trimmed && trimmed.length >= 2 && trimmed !== deleteText) {
      const trimSliceAtCharPos = actualParaText.slice(charPos, charPos + trimmed.length);
      if (trimSliceAtCharPos === trimmed) {
        log('[RedlineInserter] resolveReplaceSearchText: trimmed exact at charPos', {
          charPos,
          trimmedSnippet: trimmed.slice(0, 40),
        });
        return { searchText: trimmed, effectiveCharPos: charPos };
      }
      const trimIdx = window.indexOf(trimmed);
      if (trimIdx !== -1) {
        const effectiveCharPos = windowStart + trimIdx;
        log('[RedlineInserter] resolveReplaceSearchText: trimmed relocated in window', {
          charPos,
          effectiveCharPos,
          trimmedSnippet: trimmed.slice(0, 40),
        });
        return { searchText: trimmed, effectiveCharPos };
      }
    }

    log('[RedlineInserter] resolveReplaceSearchText: fallback slice (may be garbled)', {
      charPos,
      exactSliceSnippet: exactSlice.slice(0, 40),
      deleteTextSnippet: deleteText.slice(0, 40),
    });
    return { searchText: exactSlice || deleteText, effectiveCharPos: charPos };
  }

  /**
   * Build progressively broader search anchors when the initial `Range.search` returns 0 hits.
   *
   * Triggered when Word's *visible* text diverges from `actualParaText` (e.g. paragraph contains
   * counterparty `w:ins`/`w:del` so Word's surface text is longer than the OOXML accepted text).
   * In that situation the initial anchor may sit inside a track-change boundary that `Range.search`
   * cannot match. The retries try:
   *   1. Trimmed text (drops leading/trailing whitespace — common cause of search misses).
   *   2. Trimmed text extended with one preceding word from `actualParaText` (more context).
   *
   * Each candidate is unique-aware: we keep it only if it differs from already-tried anchors.
   */
  private static buildReplaceFallbackAnchors(
    actualParaText: string,
    effectiveCharPos: number,
    deleteText: string,
    primarySearchText: string,
  ): string[] {
    const candidates: string[] = [];
    const seen = new Set<string>([primarySearchText]);

    const push = (s: string | null | undefined) => {
      if (!s) return;
      if (s.length < 2) return;
      if (seen.has(s)) return;
      seen.add(s);
      candidates.push(s);
    };

    const trimmed = deleteText.trim();
    push(trimmed);
    const trimmedSearchable = stripWordSearchNoise(trimmed);
    if (trimmedSearchable && trimmedSearchable !== trimmed) {
      push(trimmedSearchable);
    }
    const collapsed = collapseAnchorPunctuation(deleteText);
    if (collapsed && collapsed !== trimmed) {
      push(collapsed);
    }
    if (collapsed.length >= 8) {
      push(collapsed.slice(-Math.min(22, collapsed.length)));
    }

    const prefixSlice = actualParaText.slice(Math.max(0, effectiveCharPos - 30), effectiveCharPos);
    const lastSpace = prefixSlice.lastIndexOf(' ');
    const oneWordBefore = lastSpace >= 0 ? prefixSlice.slice(lastSpace + 1) : prefixSlice;
    if (oneWordBefore && trimmed) {
      push(`${oneWordBefore} ${trimmed}`.trim());
    }

    return candidates;
  }

  /**
   * Build the shortest unique anchor that contains `targetText` by expanding word-by-word into
   * the surrounding paragraph text. Returns the parent anchor (unique in `actualParaText`) and
   * the inner text (what to search for inside the matched parent Range in Word).
   *
   * Why this is needed: `Range.search` runs against Word's *visible* text, which includes the
   * struck-through text of any pending counterparty track-change deletions. When a short anchor
   * like `" היזם"` matches in those deletions, `occIdx` (computed against `actualParaText`,
   * which excludes deletions) selects the wrong occurrence and the edit lands in the wrong spot.
   *
   * A longer surrounding-words anchor is highly unlikely to coincide with a struck-through
   * deletion (it would require the counterparty to have deleted the exact same multi-word phrase).
   * Once the unique parent Range is found in Word, an inner search inside it gives a single
   * unambiguous Range pointing at the actual edit target.
   *
   * Returns null only when even a very long anchor can't be made unique (extremely rare).
   */
  private static findUniqueContextAnchor(
    actualParaText: string,
    charPos: number,
    targetText: string,
  ): { anchor: string; innerText: string; expandedBy: number } | null {
    if (!targetText) return null;
    const MAX_ANCHOR = 250;

    // Find where targetText actually exists near charPos. Coalesce can shift charPos by ±2.
    const trimmed = targetText.trim();
    const candidates: string[] = [targetText];
    if (trimmed && trimmed !== targetText) candidates.push(trimmed);

    let foundStart = -1;
    let foundInner = '';
    for (const candidate of candidates) {
      const exactSlice = actualParaText.slice(charPos, charPos + candidate.length);
      if (exactSlice === candidate) {
        foundStart = charPos;
        foundInner = candidate;
        break;
      }
      const driftBudget = Math.max(8, Math.ceil(candidate.length / 2));
      const winStart = Math.max(0, charPos - driftBudget);
      const winEnd = Math.min(
        actualParaText.length,
        charPos + candidate.length + driftBudget,
      );
      const localIdx = actualParaText.slice(winStart, winEnd).indexOf(candidate);
      if (localIdx !== -1) {
        foundStart = winStart + localIdx;
        foundInner = candidate;
        break;
      }
    }
    if (foundStart < 0) {
      log('[RedlineInserter] findUniqueContextAnchor: target not found near charPos', {
        charPos,
        targetTextSnippet: targetText.slice(0, 40),
      });
      return null;
    }
    let foundEnd = foundStart + foundInner.length;

    // Already unique → no expansion needed.
    if (RedlineInserter.countOccurrencesIn(actualParaText, foundInner) === 1) {
      log('[RedlineInserter] findUniqueContextAnchor: already unique (no expansion)', {
        charPos,
        foundStart,
        innerSnippet: foundInner.slice(0, 40),
        occCount: 1,
      });
      return { anchor: foundInner, innerText: foundInner, expandedBy: 0 };
    }

    let leftPos = foundStart;
    let rightPos = foundEnd;

    // Expand by one whole word + its preceding whitespace.
    const stepLeft = (): boolean => {
      let p = leftPos;
      while (p > 0 && /\s/.test(actualParaText[p - 1])) p--;
      while (p > 0 && /\S/.test(actualParaText[p - 1])) p--;
      if (p < leftPos) {
        leftPos = p;
        return true;
      }
      return false;
    };
    const stepRight = (): boolean => {
      let p = rightPos;
      while (p < actualParaText.length && /\s/.test(actualParaText[p])) p++;
      while (p < actualParaText.length && /\S/.test(actualParaText[p])) p++;
      if (p > rightPos) {
        rightPos = p;
        return true;
      }
      return false;
    };

    let initialLen = foundInner.length;
    while (rightPos - leftPos < MAX_ANCHOR) {
      const grewLeft = stepLeft();
      let candidate = actualParaText.slice(leftPos, rightPos);
      if (
        candidate.length > initialLen &&
        RedlineInserter.countOccurrencesIn(actualParaText, candidate) === 1
      ) {
        log('[RedlineInserter] findUniqueContextAnchor: expanded left → unique', {
          charPos,
          foundStart,
          innerSnippet: foundInner.slice(0, 40),
          anchorSnippet: candidate.slice(0, 80),
          expandedBy: candidate.length - initialLen,
        });
        return {
          anchor: candidate,
          innerText: foundInner,
          expandedBy: candidate.length - initialLen,
        };
      }
      const grewRight = stepRight();
      candidate = actualParaText.slice(leftPos, rightPos);
      if (
        candidate.length > initialLen &&
        RedlineInserter.countOccurrencesIn(actualParaText, candidate) === 1
      ) {
        log('[RedlineInserter] findUniqueContextAnchor: expanded right → unique', {
          charPos,
          foundStart,
          innerSnippet: foundInner.slice(0, 40),
          anchorSnippet: candidate.slice(0, 80),
          expandedBy: candidate.length - initialLen,
        });
        return {
          anchor: candidate,
          innerText: foundInner,
          expandedBy: candidate.length - initialLen,
        };
      }
      if (!grewLeft && !grewRight) break;
    }

    log('[RedlineInserter] findUniqueContextAnchor: failed to make unique', {
      charPos,
      foundStart,
      innerSnippet: foundInner.slice(0, 40),
      finalAnchorLen: rightPos - leftPos,
      occCount: RedlineInserter.countOccurrencesIn(
        actualParaText,
        actualParaText.slice(leftPos, rightPos),
      ),
    });
    return null;
  }

  /**
   * Merge fragmented delete/replace ops from HTML (e.g. tail split into space + בנכס" + ".").
   */
  private static coalesceParsedOps(ops: ParsedHtmlOp[], actualParaText: string): ParsedHtmlOp[] {
    const sorted = [...ops].sort((a, b) => a.charPos - b.charPos);
    const out: ParsedHtmlOp[] = [];

    for (const op of sorted) {
      const prev = out[out.length - 1];
      if (!prev) {
        out.push({ ...op });
        continue;
      }

      if (
        prev.kind === 'delete' &&
        op.kind === 'delete' &&
        prev.charPos + prev.text.length === op.charPos
      ) {
        prev.text += op.text;
        continue;
      }

      if (prev.kind === 'delete' && op.kind === 'replace') {
        const prevEnd = prev.charPos + prev.text.length;
        if (op.charPos >= prev.charPos && op.charPos <= prevEnd + 2) {
          const gap =
            op.charPos > prevEnd ? actualParaText.slice(prevEnd, op.charPos) : '';
          out[out.length - 1] = {
            kind: 'replace',
            charPos: prev.charPos,
            deleteText: prev.text + gap + op.deleteText,
            insertText: op.insertText,
          };
          continue;
        }
      }

      // Multiple preview <ins> blocks at the same accepted-text position (e.g. user
      // "קטן" + AI " של ") must become one Word insertion — otherwise both anchor on
      // the same suffix word and land in the wrong order with broken spacing.
      if (
        prev.kind === 'insert' &&
        op.kind === 'insert' &&
        prev.charPos === op.charPos
      ) {
        prev.text += op.text;
        continue;
      }

      out.push({ ...op });
    }

    return out;
  }

  /**
   * Split text into chunks that fit Word.js Range.search (255-char limit).
   * Prefers word boundaries so each chunk is a searchable phrase.
   */
  private static splitTextForWordSearch(text: string, maxLen: number): string[] {
    if (!text) return [];
    if (text.length <= maxLen) return [text];

    const chunks: string[] = [];
    let rest = text;
    while (rest.length > 0) {
      if (rest.length <= maxLen) {
        chunks.push(rest);
        break;
      }
      let cut = maxLen;
      const spaceIdx = rest.lastIndexOf(' ', maxLen);
      if (spaceIdx > Math.floor(maxLen * 0.4)) {
        cut = spaceIdx;
      }
      const chunk = rest.slice(0, cut);
      if (!chunk) break;
      chunks.push(chunk);
      rest = rest.slice(cut);
      if (rest.startsWith(' ')) rest = rest.slice(1);
    }
    return chunks;
  }

  /**
   * Break delete/replace ops whose search anchor exceeds Word.js search limits into
   * smaller word-boundary chunks. Coalesce can merge many trailing deletions into one
   * 400+ char op that Range.search rejects.
   */
  private static splitOversizedParsedOps(ops: ParsedHtmlOp[]): ParsedHtmlOp[] {
    const maxLen = WORD_JS_MAX_SEARCH_STRING_LENGTH;
    const out: ParsedHtmlOp[] = [];

    for (const op of ops) {
      if (op.kind === 'delete' && op.text.length > maxLen) {
        const subChunks = RedlineInserter.splitTextForWordSearch(op.text, maxLen);
        let offset = op.charPos;
        for (const chunk of subChunks) {
          out.push({ kind: 'delete', charPos: offset, text: chunk });
          offset += chunk.length;
        }
        continue;
      }

      if (op.kind === 'replace' && op.deleteText.length > maxLen) {
        const subChunks = RedlineInserter.splitTextForWordSearch(op.deleteText, maxLen);
        let offset = op.charPos;
        for (let i = 0; i < subChunks.length; i++) {
          const chunk = subChunks[i];
          if (i === 0) {
            out.push({
              kind: 'replace',
              charPos: offset,
              deleteText: chunk,
              insertText: op.insertText,
            });
          } else {
            out.push({ kind: 'delete', charPos: offset, text: chunk });
          }
          offset += chunk.length;
        }
        continue;
      }

      out.push(op);
    }

    return out.sort((a, b) => a.charPos - b.charPos);
  }

  /** True when every op's search anchor fits Word.js Range.search after optional splitting. */
  private static parsedOpsFitWordSearchLimit(ops: ParsedHtmlOp[]): boolean {
    const split = RedlineInserter.splitOversizedParsedOps(ops);
    return split.every((op) => {
      const anchor = RedlineInserter.searchTextForOp(op);
      return anchor.length === 0 || anchor.length <= WORD_JS_MAX_SEARCH_STRING_LENGTH;
    });
  }

  /** Extract &lt;w:p&gt; from generator output (document root or bare paragraph). */
  static extractParagraphXmlFromEditedOoxml(editedOoxml: string): string | null {
    const trimmed = (editedOoxml || '').trim();
    if (!trimmed) return null;
    const stripped = trimmed.replace(/^<\?xml[^?]*\?>\s*/i, '');
    if (/^<w:p[\s>]/i.test(stripped)) {
      return RedlineInserter.ensureXmlnsWOnParagraphXml(stripped);
    }
    try {
      // Same namespace-wrapper retry as preview: WebKit/Word Mac fragments often
      // omit xmlns:w, which makes a raw DOMParser fail and used to skip insertOoxml.
      const doc = TrackChangesParser.parseOoxml(trimmed);
      if (!doc) return null;
      const wNs = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
      const root = doc.documentElement;
      const p =
        root.localName === 'p'
          ? root
          : doc.getElementsByTagNameNS(wNs, 'p').item(0) ||
            (root.querySelector('p') as Element | null);
      if (!p) return null;
      return RedlineInserter.ensureXmlnsWOnParagraphXml(
        new XMLSerializer().serializeToString(p),
      );
    } catch {
      return null;
    }
  }

  private static ensureXmlnsWOnParagraphXml(xml: string): string {
    if (!xml || /xmlns:w=/i.test(xml)) return xml;
    const ns = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
    const stripped = xml.replace(/^<\?xml[^?]*\?>\s*/i, '');
    const decl = xml.slice(0, xml.length - stripped.length);
    return decl + stripped.replace(/^(<[^\s/>]+)/, `$1 xmlns:w="${ns}"`);
  }

  /**
   * Replace the target paragraph with edited OOXML (preserves w:ins/w:del structure from preview).
   * Preferred over incremental search when full paragraph XML is available.
   */
  /**
   * Snapshot tracked-change date/type pairs for undo matching.
   * Word Win32 sometimes throws ItemNotFound on getTrackedChanges() for paragraphs
   * that already contain counterparty revisions — treat as empty and rely on apply window.
   */
  private static async collectTrackedChangeFingerprints(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    targetParagraph: any,
    context: Word.RequestContext,
  ): Promise<TrackedChangeFingerprint[]> {
    try {
      const trackedChanges = targetParagraph.getTrackedChanges();
      trackedChanges.load('items');
      await context.sync();
      const items = trackedChanges.items || [];
      for (const tc of items) {
        tc.load(['date', 'type']);
      }
      await context.sync();
      return items.map((tc: { date?: Date | string; type?: string }) => ({
        dateIso: tc.date ? new Date(tc.date).toISOString() : '',
        type: String(tc.type ?? ''),
      }));
    } catch (error) {
      logWarn('[RedlineInserter] collectTrackedChangeFingerprints: unavailable — continuing without fingerprints', {
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : typeof error,
      });
      return [];
    }
  }

  static async applyParagraphFromEditedOoxml(
    editedOoxml: string,
    paragraphIndex?: number,
    resolveOptions?: ParagraphApplyResolveOptions,
  ): Promise<ApplyParagraphResult> {
    let effectiveParagraphIndex = paragraphIndex;
    if (resolveOptions) {
      const resolved = await DocumentReader.resolveParagraphForApply(
        paragraphIndex,
        resolveOptions,
      );
      if (resolved != null) {
        effectiveParagraphIndex = resolved;
      } else if (
        resolveOptions.trustBodyParagraphIndex === false ||
        resolveOptions.ooxmlParagraphIndex != null
      ) {
        effectiveParagraphIndex = undefined;
      }
    }

    log('[RedlineInserter] applyParagraphFromEditedOoxml: START', {
      paragraphIndex: paragraphIndex ?? null,
      effectiveParagraphIndex: effectiveParagraphIndex ?? null,
      editedOoxmlLen: editedOoxml?.length ?? 0,
    });

    const paragraphXml = RedlineInserter.extractParagraphXmlFromEditedOoxml(editedOoxml);
    if (!paragraphXml) {
      logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: could not extract w:p — will use incremental');
      return { applied: false };
    }

    const structureBefore = RedlineInserter.auditOoxmlParagraphStructure(paragraphXml);
    log('[RedlineInserter] applyParagraphFromEditedOoxml: OOXML structure audit', structureBefore);

    // Word's Range.insertOoxml rejects paragraphs that contain a <w:ins> nested directly
    // inside another <w:ins> (counterparty wrapping AI). Skip OOXML path in that case;
    // incremental search-based apply handles it correctly.
    if (structureBefore.hasNestedInsInsideIns) {
      log('[RedlineInserter] applyParagraphFromEditedOoxml: skipping (nested w:ins inside w:ins is not supported by insertOoxml) — will use incremental', {
        nestedInsCount: structureBefore.nestedInsCount,
        nestedInsPairs: structureBefore.nestedInsPairs,
      });
      return { applied: false };
    }

    const packageXml = OoxmlPackageWrapper.wrapParagraphInPackage(paragraphXml);
    if (!packageXml) {
      logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: package wrap failed — will use incremental');
      return { applied: false };
    }

    try {
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      if (!trackChangesEnabled) {
        logError('[RedlineInserter] applyParagraphFromEditedOoxml: track changes unavailable');
        return { applied: false };
      }

      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;
        let resolvedParagraphIndex = -1;
        if (
          effectiveParagraphIndex != null &&
          effectiveParagraphIndex >= 0 &&
          effectiveParagraphIndex < paragraphs.items.length
        ) {
          resolvedParagraphIndex = effectiveParagraphIndex;
          targetParagraph = paragraphs.items[effectiveParagraphIndex];
        }

        // Intentionally do NOT silently default to paragraph 0 when paragraphIndex
        // is null / out-of-range. Doing so previously caused the AI "Submit to Response"
        // flow on unmatched clauses (clause.paragraph_index == null) to attempt the
        // OOXML insert against the document title, which Word rejects with a
        // GeneralException. Fail explicitly so the caller can resolve the paragraph
        // (selection, content search) or surface a clear error to the user.
        if (!targetParagraph || resolvedParagraphIndex < 0) {
          logError('[RedlineInserter] applyParagraphFromEditedOoxml: paragraph not found — refusing to default to paragraph 0', {
            paragraphIndex: paragraphIndex ?? null,
            effectiveParagraphIndex: effectiveParagraphIndex ?? null,
            paragraphCount: paragraphs.items.length,
          });
          return { applied: false };
        }

        targetParagraph.load('text');
        await context.sync();
        const wordTextBefore = normalizeWs(targetParagraph.text || '');
        const originalParagraphText = wordTextBefore;
        let originalParagraphOoxml: string | null = null;
        let originalParagraphOoxmlPackage: string | null = null;
        try {
          const ooxmlProxy = targetParagraph.getOoxml();
          await context.sync();
          const rawPackageXml = ooxmlProxy?.value;
          if (typeof rawPackageXml === 'string' && rawPackageXml.length > 0) {
            const prunedPackage = OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(
              rawPackageXml,
              originalParagraphText,
            );
            if (prunedPackage && prunedPackage.length > 0) {
              originalParagraphOoxmlPackage = prunedPackage;
            }
          }
          originalParagraphOoxml = DocumentReader.singleParagraphXmlFromGetOoxmlResult(
            ooxmlProxy,
            originalParagraphText,
          );
        } catch (ooxmlErr) {
          logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: could not capture original OOXML', {
            error: ooxmlErr instanceof Error ? ooxmlErr.message : String(ooxmlErr),
          });
        }
        log('[RedlineInserter] applyParagraphFromEditedOoxml: Word paragraph BEFORE insertOoxml', {
          paragraphIndex: paragraphIndex ?? null,
          effectiveParagraphIndex: effectiveParagraphIndex ?? null,
          wordTextLen: wordTextBefore.length,
          wordTextSnippet: wordTextBefore.slice(0, 120),
          packageXmlLen: packageXml.length,
          paragraphXmlLen: paragraphXml.length,
        });

        const beforeFingerprints =
          await RedlineInserter.collectTrackedChangeFingerprints(targetParagraph, context);

        const range = targetParagraph.getRange('Whole');
        const rangeWithOoxml = range as Word.Range & {
          insertOoxml?: (xml: string, loc: Word.InsertLocation) => void;
        };
        if (typeof rangeWithOoxml.insertOoxml !== 'function') {
          logError('[RedlineInserter] applyParagraphFromEditedOoxml: insertOoxml not supported');
          return { applied: false };
        }

        try {
          const applyStartedAtMs = Date.now();
          rangeWithOoxml.insertOoxml(packageXml, Word.InsertLocation.replace);
          await context.sync();
          const applyFinishedAtMs = Date.now();

          const afterFingerprints =
            await RedlineInserter.collectTrackedChangeFingerprints(targetParagraph, context);
          const revertContext = buildSubmitRevertContext(
            applyStartedAtMs,
            applyFinishedAtMs,
            beforeFingerprints,
            afterFingerprints,
            resolvedParagraphIndex,
          );
          revertContext.originalParagraphText = originalParagraphText;
          if (originalParagraphOoxml != null) {
            revertContext.originalParagraphOoxml = originalParagraphOoxml;
          }
          if (originalParagraphOoxmlPackage != null) {
            revertContext.originalParagraphOoxmlPackage = originalParagraphOoxmlPackage;
          }

          targetParagraph.load('text');
          await context.sync();
          const wordTextAfter = normalizeWs(targetParagraph.text || '');
          log('[RedlineInserter] applyParagraphFromEditedOoxml: SUCCESS via insertOoxml', {
            paragraphIndex: resolvedParagraphIndex,
            wordTextBeforeLen: wordTextBefore.length,
            wordTextAfterLen: wordTextAfter.length,
            wordTextAfterSnippet: wordTextAfter.slice(0, 120),
            applyStartedAtMs,
            applyFinishedAtMs,
            addedFingerprints: revertContext.addedFingerprints.length,
          });
          return { applied: true, revertContext };
        } catch (insertErr) {
          logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: insertOoxml threw inside Word.run', {
            error: insertErr instanceof Error ? insertErr.message : String(insertErr),
            errorName: insertErr instanceof Error ? insertErr.name : typeof insertErr,
            paragraphIndex: paragraphIndex ?? null,
            structureAudit: structureBefore,
            wordTextBeforeLen: wordTextBefore.length,
            packageXmlLen: packageXml.length,
            packageXmlPreview: packageXml.slice(0, 400),
          });
          return { applied: false };
        }
      });
    } catch (error) {
      // insertOoxml is strict about supported OOXML; fall through to incremental.
      logWarn('[RedlineInserter] applyParagraphFromEditedOoxml: outer catch — will use incremental', {
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : typeof error,
        structureAudit: structureBefore,
      });
      return { applied: false };
    }
  }

  /**
   * Submit as-is: replace the client-request paragraph with source plain text as a single
   * tracked deletion + insertion (no word-level diff through nested counterparty markup).
   */
  static async applySubmitAsIs(
    acceptedPlainText: string,
    newPlainText: string,
    paragraphIndex?: number,
    authorName: string = 'User',
    sourceParagraphOoxml?: string | null,
    resolveOptions?: ParagraphApplyResolveOptions,
  ): Promise<ApplyParagraphResult> {
    const normAccepted = normalizeWs(acceptedPlainText);
    const normNew = normalizeWs(newPlainText);
    if (!normAccepted || !normNew) {
      logError('[RedlineInserter] applySubmitAsIs: missing accepted or new text');
      return { applied: false };
    }

    let effectiveParagraphIndex = paragraphIndex;
    const mergedResolve: ParagraphApplyResolveOptions = {
      searchText: resolveOptions?.searchText ?? normAccepted,
      ...resolveOptions,
    };
    const resolved = await DocumentReader.resolveParagraphForApply(
      paragraphIndex,
      mergedResolve,
    );
    if (resolved != null) {
      effectiveParagraphIndex = resolved;
    } else if (
      mergedResolve.trustBodyParagraphIndex === false ||
      mergedResolve.ooxmlParagraphIndex != null
    ) {
      effectiveParagraphIndex = undefined;
    }

    const paragraphOoxml = TrackChangesParser.createSubmitAsIsParagraphOoxml(
      normAccepted,
      normNew,
      authorName,
      sourceParagraphOoxml,
    );

    log('[RedlineInserter] applySubmitAsIs: built flat paragraph OOXML', {
      paragraphIndex: paragraphIndex ?? null,
      effectiveParagraphIndex: effectiveParagraphIndex ?? null,
      authorName,
      acceptedLen: normAccepted.length,
      newLen: normNew.length,
      paragraphOoxmlLen: paragraphOoxml.length,
    });

    const ooxmlResult = await RedlineInserter.applyParagraphFromEditedOoxml(
      paragraphOoxml,
      effectiveParagraphIndex,
    );
    if (ooxmlResult.applied) {
      log('[RedlineInserter] applySubmitAsIs: applied via insertOoxml');
      await RedlineInserter.selectParagraphForSubmitAsIs(effectiveParagraphIndex, normAccepted);
      return ooxmlResult;
    }

    logWarn('[RedlineInserter] applySubmitAsIs: insertOoxml failed — trying whole-paragraph insertText');
    const insertTextResult = await RedlineInserter.applySubmitAsIsViaInsertText(
      normAccepted,
      normNew,
      effectiveParagraphIndex,
      mergedResolve,
    );
    if (insertTextResult.applied) {
      await RedlineInserter.selectParagraphForSubmitAsIs(effectiveParagraphIndex, normAccepted);
    }
    return insertTextResult;
  }

  /** Keep the clause paragraph selected after submit-as-is (matches incremental apply). */
  private static async selectParagraphForSubmitAsIs(
    paragraphIndex?: number,
    acceptedPlainText?: string,
  ): Promise<void> {
    try {
      await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const items = paragraphs.items;
        for (const para of items) {
          para.load('text');
        }
        await context.sync();

        const normAccepted = acceptedPlainText ? normalizeWs(acceptedPlainText) : '';
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;

        if (
          paragraphIndex != null &&
          paragraphIndex >= 0 &&
          paragraphIndex < items.length
        ) {
          targetParagraph = items[paragraphIndex];
        } else if (normAccepted) {
          for (let pi = 0; pi < items.length; pi++) {
            if (normalizeWs(items[pi].text || '') === normAccepted) {
              targetParagraph = items[pi];
              break;
            }
          }
        }

        if (!targetParagraph) return;

        targetParagraph.getRange('Whole').select();
        await context.sync();
      });
    } catch (error) {
      logWarn('[RedlineInserter] selectParagraphForSubmitAsIs failed', {
        error: error instanceof Error ? error.message : String(error),
      });
    }
  }

  /** Fallback when flat OOXML insertOoxml is unavailable. */
  private static async applySubmitAsIsViaInsertText(
    acceptedPlainText: string,
    newPlainText: string,
    paragraphIndex?: number,
    resolveOptions?: ParagraphApplyResolveOptions,
  ): Promise<ApplyParagraphResult> {
    try {
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      if (!trackChangesEnabled) {
        logError('[RedlineInserter] applySubmitAsIsViaInsertText: track changes unavailable');
        return { applied: false };
      }

      let effectiveParagraphIndex = paragraphIndex;
      if (resolveOptions) {
        const resolved = await DocumentReader.resolveParagraphForApply(
          paragraphIndex,
          resolveOptions,
        );
        if (resolved != null) {
          effectiveParagraphIndex = resolved;
        } else if (
          resolveOptions.trustBodyParagraphIndex === false ||
          resolveOptions.ooxmlParagraphIndex != null
        ) {
          effectiveParagraphIndex = undefined;
        }
      }

      return await Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const items = paragraphs.items;
        for (const para of items) {
          para.load('text');
        }
        await context.sync();

        const normAccepted = normalizeWs(acceptedPlainText);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;
        let resolvedParagraphIndex = -1;

        const exactIndices: number[] = [];
        for (let pi = 0; pi < items.length; pi++) {
          if (normalizeWs(items[pi].text || '') === normAccepted) exactIndices.push(pi);
        }

        if (exactIndices.length === 1) {
          resolvedParagraphIndex = exactIndices[0];
          targetParagraph = items[exactIndices[0]];
        } else if (
          effectiveParagraphIndex != null &&
          effectiveParagraphIndex >= 0 &&
          effectiveParagraphIndex < items.length
        ) {
          resolvedParagraphIndex = effectiveParagraphIndex;
          targetParagraph = items[effectiveParagraphIndex];
        } else if (exactIndices.length > 0) {
          resolvedParagraphIndex = exactIndices[0];
          targetParagraph = items[exactIndices[0]];
        }

        if (!targetParagraph || resolvedParagraphIndex < 0) {
          logError('[RedlineInserter] applySubmitAsIsViaInsertText: paragraph not found', {
            paragraphIndex: paragraphIndex ?? null,
            effectiveParagraphIndex: effectiveParagraphIndex ?? null,
          });
          return { applied: false };
        }

        // Capture the original paragraph text from the document before we replace it.
        // This is the authoritative "before" text for Win32 undo (since fingerprints won't
        // be available on Win32, we restore the paragraph via TC-off + insertOoxml/insertText).
        const originalParagraphText: string = targetParagraph.text ?? acceptedPlainText;

        // Also capture the SINGLE target <w:p> OOXML so the undo path can restore pre-existing
        // tracked changes (e.g. counterparty edits). We MUST store just one <w:p> element here,
        // never a <w:body> wrapper, because the revert path feeds this string into
        // `OoxmlPackageWrapper.wrapParagraphInPackage(...)` which embeds the input inside
        // `<w:document><w:body>...</w:body></w:document>`. If the input is already a <w:body>
        // (multi-paragraph) wrapper, the result is nested <w:body><w:body>...</w:body></w:body>
        // which is invalid OOXML and is rejected by `insertOoxml` on Word Win32 with
        // `GeneralException`. The fallback to `insertText` then loses all tracked changes,
        // producing the observed Windows undo bug where the original tracked changes vanish.
        //
        // On Mac, `paragraph.getOoxml()` typically returns a clean single <w:p>. On Win32, it
        // can include trailing section context (extra empty paragraphs + <w:sectPr>) — we strip
        // that here by selecting the <w:p> whose text matches the captured paragraph text.
        // Capture BOTH the full pkg:package (preferred for Win32 undo via round-trip) AND the
        // extracted single <w:p> (fallback wrapped via OoxmlPackageWrapper). The full package
        // is trimmed to keep only the target paragraph in <w:body> so it can be re-inserted
        // without duplicating sibling paragraphs / section properties that Win32 sometimes
        // bundles into paragraph.getOoxml().
        let originalParagraphOoxml: string | null = null;
        let originalParagraphOoxmlPackage: string | null = null;
        try {
          const ooxmlProxy = targetParagraph.getOoxml();
          await context.sync();
          const rawPackageXml = ooxmlProxy?.value;
          if (typeof rawPackageXml === 'string' && rawPackageXml.length > 0) {
            const prunedPackage = OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(
              rawPackageXml,
              originalParagraphText,
            );
            if (prunedPackage && prunedPackage.length > 0) {
              originalParagraphOoxmlPackage = prunedPackage;
            }
          }
          originalParagraphOoxml = DocumentReader.singleParagraphXmlFromGetOoxmlResult(
            ooxmlProxy,
            originalParagraphText,
          );
          log('[RedlineInserter] applySubmitAsIsViaInsertText: captured original paragraph OOXML', {
            paragraphIndex: resolvedParagraphIndex,
            paragraphOoxmlLen: originalParagraphOoxml?.length ?? 0,
            paragraphOoxmlPackageLen: originalParagraphOoxmlPackage?.length ?? 0,
            isSingleParagraphOoxml:
              originalParagraphOoxml != null &&
              originalParagraphOoxml.trimStart().startsWith('<w:p'),
            hasFullPackage: originalParagraphOoxmlPackage != null,
          });
        } catch (ooxmlErr) {
          logWarn('[RedlineInserter] applySubmitAsIsViaInsertText: could not capture OOXML — text-only undo', {
            error: ooxmlErr instanceof Error ? ooxmlErr.message : String(ooxmlErr),
          });
        }

        log('[RedlineInserter] applySubmitAsIsViaInsertText: captured original paragraph text', {
          paragraphIndex: resolvedParagraphIndex,
          originalTextLen: originalParagraphText.length,
          originalTextSnippet: originalParagraphText.slice(0, 120),
          hasOriginalOoxml: originalParagraphOoxml != null,
        });

        const beforeFingerprints =
          await RedlineInserter.collectTrackedChangeFingerprints(targetParagraph, context);

        const range = targetParagraph.getRange('Whole');
        const applyStartedAtMs = Date.now();
        range.insertText(prepareBidiTextForWordInsert(newPlainText), Word.InsertLocation.replace);
        await context.sync();
        const applyFinishedAtMs = Date.now();

        const afterFingerprints =
          await RedlineInserter.collectTrackedChangeFingerprints(targetParagraph, context);
        const revertContext = buildSubmitRevertContext(
          applyStartedAtMs,
          applyFinishedAtMs,
          beforeFingerprints,
          afterFingerprints,
          resolvedParagraphIndex,
        );
        // Attach original text and OOXML for Win32 undo path.
        revertContext.originalParagraphText = originalParagraphText;
        if (originalParagraphOoxml != null) {
          revertContext.originalParagraphOoxml = originalParagraphOoxml;
        }
        if (originalParagraphOoxmlPackage != null) {
          revertContext.originalParagraphOoxmlPackage = originalParagraphOoxmlPackage;
        }

        log('[RedlineInserter] applySubmitAsIsViaInsertText: replaced paragraph via insertText', {
          paragraphIndex: resolvedParagraphIndex,
          applyStartedAtMs,
          applyFinishedAtMs,
          addedFingerprints: revertContext.addedFingerprints.length,
          originalParagraphTextLen: originalParagraphText.length,
          originalParagraphOoxmlLen: originalParagraphOoxml?.length ?? 0,
          originalParagraphOoxmlPackageLen: originalParagraphOoxmlPackage?.length ?? 0,
        });
        return { applied: true, revertContext };
      });
    } catch (error) {
      logError('[RedlineInserter] applySubmitAsIsViaInsertText failed', error);
      return { applied: false };
    }
  }

  /**
   * Deep OOXML structure audit for submit-to-response diagnostics.
   * Logs ins/del counts, authors, nesting, and direct paragraph children.
   */
  static auditOoxmlParagraphStructure(paragraphXml: string): {
    parseError: boolean;
    insCount: number;
    delCount: number;
    nestedInsCount: number;
    hasNestedInsInsideIns: boolean;
    authors: string[];
    directChildTags: string[];
    insAuthors: string[];
    delAuthors: string[];
    nestedInsPairs: Array<{ outerAuthor: string; innerAuthor: string }>;
    hasFldChar: boolean;
    hasInstrText: boolean;
    xmlLen: number;
  } {
    const empty = {
      parseError: true,
      insCount: 0,
      delCount: 0,
      nestedInsCount: 0,
      hasNestedInsInsideIns: false,
      authors: [] as string[],
      directChildTags: [] as string[],
      insAuthors: [] as string[],
      delAuthors: [] as string[],
      nestedInsPairs: [] as Array<{ outerAuthor: string; innerAuthor: string }>,
      hasFldChar: false,
      hasInstrText: false,
      xmlLen: paragraphXml?.length ?? 0,
    };
    try {
      const xmlDoc = new DOMParser().parseFromString(paragraphXml, 'text/xml');
      if (xmlDoc.querySelector('parsererror')) return empty;
      const W_NS = 'http://schemas.openxmlformats.org/wordprocessingml/2006/main';
      const getAuthor = (el: Element) =>
        el.getAttribute('w:author') || el.getAttributeNS(W_NS, 'author') || '';

      let insCount = 0;
      let delCount = 0;
      let nestedInsCount = 0;
      const insAuthors: string[] = [];
      const delAuthors: string[] = [];
      const nestedInsPairs: Array<{ outerAuthor: string; innerAuthor: string }> = [];

      const inspect = (el: Element, insideIns: boolean, parentInsAuthor = '') => {
        const local = el.localName || el.tagName.split(':').pop() || '';
        const isIns = local === 'ins';
        const isDel = local === 'del';
        if (isIns) {
          insCount++;
          const author = getAuthor(el);
          if (author) insAuthors.push(author);
          if (insideIns) {
            nestedInsCount++;
            nestedInsPairs.push({ outerAuthor: parentInsAuthor, innerAuthor: author });
          }
        }
        if (isDel) {
          delCount++;
          const author = getAuthor(el);
          if (author) delAuthors.push(author);
        }
        const childInside = insideIns || isIns;
        const nextParentAuthor = isIns ? getAuthor(el) : parentInsAuthor;
        for (const child of Array.from(el.childNodes)) {
          if (child.nodeType === Node.ELEMENT_NODE) {
            inspect(child as Element, childInside, nextParentAuthor);
          }
        }
      };
      const root = xmlDoc.documentElement;
      inspect(root, false);

      const directChildTags = Array.from(root.childNodes)
        .filter((n) => n.nodeType === Node.ELEMENT_NODE)
        .map((n) => (n as Element).localName || (n as Element).tagName);

      const authors = [...new Set([...insAuthors, ...delAuthors])];
      return {
        parseError: false,
        insCount,
        delCount,
        nestedInsCount,
        hasNestedInsInsideIns: nestedInsCount > 0,
        authors,
        directChildTags,
        insAuthors,
        delAuthors,
        nestedInsPairs,
        hasFldChar: /<w:fldChar\b/i.test(paragraphXml),
        hasInstrText: /<w:instrText\b/i.test(paragraphXml),
        xmlLen: paragraphXml.length,
      };
    } catch {
      return empty;
    }
  }

  /** Compare search needle occurrence counts across baseline texts. */
  private static auditSearchAnchor(
    needle: string,
    actualParaText: string,
    apiParaText: string,
    charPos: number,
  ) {
    const occActual = findAllOccurrenceIndices(actualParaText, needle);
    const occApi = findAllOccurrenceIndices(apiParaText, needle);
    const occIdxActual = RedlineInserter.countOccurrencesBefore(actualParaText, needle, charPos);
    const occIdxApi = RedlineInserter.countOccurrencesBefore(apiParaText, needle, charPos);
    return {
      needleSnippet: (needle || '').slice(0, 60),
      needleLen: needle.length,
      charPos,
      occInActualParaText: occActual.length,
      occInApiText: occApi.length,
      occIndicesActual: occActual,
      occIndicesApi: occApi,
      occIdxAtCharPosActual: occIdxActual,
      occIdxAtCharPosApi: occIdxApi,
      mismatchOccCount: occActual.length !== occApi.length,
      sliceAtCharPosActual: actualParaText.slice(charPos, charPos + needle.length),
      sliceAtCharPosApi: apiParaText.slice(charPos, charPos + needle.length),
    };
  }

  /**
   * Structured summary of parsed track-changes HTML — for submit / incremental diagnostics.
   * Exposed so CrossCheckClauseCard can log the same shape before calling RedlineInserter.
   */
  static summarizeTrackChangesHtmlOps(html: string): {
    opCount: number;
    anyOpExceedsWordJsSearchLimit: boolean;
    anyInsertMissingSearchAnchor: boolean;
    trailingInsertOnly: boolean;
    maxCharPos: number;
    ops: Array<{
      kind: string;
      charPos: number;
      searchTextLen: number;
      hasSearchAnchor: boolean;
      suffixWords: string[];
      insertTextLen?: number;
      deleteTextLen?: number;
      insertSnippet?: string;
      failureHint?: string;
    }>;
  } {
    const snippet = (s: string, max = 80) => {
      const compact = (s || '').replace(/\s+/g, ' ').trim();
      return compact.length > max ? `${compact.slice(0, max)}...` : compact;
    };
    const ops = RedlineInserter.parseHtmlOps(html);
    let maxSearchTextLen = 0;
    let maxCharPos = 0;
    let anyInsertMissingSearchAnchor = false;
    const summarized = ops.map((op) => {
      const searchText = RedlineInserter.searchTextForOp(op);
      maxSearchTextLen = Math.max(maxSearchTextLen, searchText.length);
      maxCharPos = Math.max(maxCharPos, op.charPos);
      let failureHint: string | undefined;
      if (op.kind === 'insert' && searchText.length === 0) {
        anyInsertMissingSearchAnchor = true;
        failureHint =
          op.suffixWords.length === 0
            ? 'trailing_insert_no_suffix_anchor'
            : 'insert_empty_search_anchor';
      }
      return {
        kind: op.kind,
        charPos: op.charPos,
        searchTextLen: searchText.length,
        hasSearchAnchor: searchText.length > 0,
        suffixWords: op.kind === 'insert' ? op.suffixWords : [],
        ...(op.kind === 'insert'
          ? { insertTextLen: op.text.length, insertSnippet: snippet(op.text) }
          : {}),
        ...(op.kind === 'replace'
          ? { deleteTextLen: op.deleteText.length, insertTextLen: op.insertText.length }
          : {}),
        ...(op.kind === 'delete' ? { deleteTextLen: op.text.length } : {}),
        failureHint,
      };
    });
    const trailingInsertOnly =
      ops.length === 1 &&
      ops[0].kind === 'insert' &&
      !RedlineInserter.searchTextForOp(ops[0]);
    return {
      opCount: ops.length,
      anyOpExceedsWordJsSearchLimit: maxSearchTextLen > WORD_JS_MAX_SEARCH_STRING_LENGTH,
      anyInsertMissingSearchAnchor,
      trailingInsertOnly,
      maxCharPos,
      ops: summarized,
    };
  }

  /**
   * Whether incremental apply can run: every insert without a suffix anchor must be a
   * trailing insert at paragraph end (appended via Range End, not Range.search).
   */
  private static canRunIncrementalWithOpsSummary(
    summary: ReturnType<typeof RedlineInserter.summarizeTrackChangesHtmlOps>,
    paragraphTextLen: number,
  ): boolean {
    if (summary.opCount === 0) return false;
    if (!summary.anyInsertMissingSearchAnchor) return true;
    const unanchoredInserts = summary.ops.filter(
      (op) => op.kind === 'insert' && !op.hasSearchAnchor,
    );
    return unanchoredInserts.every(
      (op) =>
        op.suffixWords.length === 0 &&
        op.charPos >= paragraphTextLen - 1,
    );
  }

  /** Whether parsed HTML ops can all use Range.search (Word.js 255-char limit). */
  private static incrementalOpsSearchLens(html: string): {
    opCount: number;
    anyOpExceedsWordJsSearchLimit: boolean;
  } {
    const summary = RedlineInserter.summarizeTrackChangesHtmlOps(html);
    return {
      opCount: summary.opCount,
      anyOpExceedsWordJsSearchLimit: summary.anyOpExceedsWordJsSearchLimit,
    };
  }

  /**
   * Incremental word-level track changes using SEARCH-BASED positioning.
   *
   * Why search-based: When Track Changes is ON, each edit creates tracked deletion
   * ranges + tracked insertion ranges. getTextRanges returns ALL of these, making
   * index-based delta tracking impossible. Search bypasses this entirely by finding
   * the target text by content, not by position.
   *
   * 1. Find paragraph by index (or text fallback)
   * 2. Accept existing track changes (clean slate)
   * 3. Compute word-level diff → group into ChangeChunks with prefix/suffix context
   * 4. Apply each chunk LEFT-TO-RIGHT using Range.search() to find target text
   * 5. Each operation is a single Word API call → word-level track changes
   */
  static async applyWordLevelChangesIncrementally(
    originalText: string,
    newText: string,
    trackChangesHtml: string,
    paragraphIndex?: number,
  ): Promise<ApplyParagraphResult> {
    try {
      const snippet = (s: string, max = 140) => {
        const compact = (s || '').replace(/\s+/g, ' ').trim();
        return compact.length > max ? `${compact.slice(0, max)}...` : compact;
      };
      log('[RedlineInserter] incremental apply: start', {
        paragraphIndexHint: paragraphIndex ?? null,
        originalTextLen: originalText?.length ?? 0,
        newTextLen: newText?.length ?? 0,
        trackChangesHtmlLen: trackChangesHtml?.length ?? 0,
        originalSnippet: snippet(originalText),
        newSnippet: snippet(newText),
      });

      // Ensure Track Changes is ON
      const trackChangesEnabled = await TrackChangesManager.ensureEnabled();
      if (!trackChangesEnabled) {
        log('[RedlineInserter] incremental apply: track changes unavailable, using regular replace');
        await this.insertAsRegularTextWithSearch(originalText, newText);
        return { applied: true };
      }


      // --- apply inside Word.run ---
      // We first read the ACTUAL paragraph text from Word, then compute the diff.
      // This avoids mismatches from XML extraction vs. actual document text.

      const incrementalResult = await Word.run(async (context) => {
        // 1. Resolve paragraph (prefer body text over stale agent index — e.g. after apply_word_structure)
        const paragraphs = context.document.body.paragraphs;

        paragraphs.load('items');
        await context.sync();

        for (const para of paragraphs.items) {
          para.load('text');
        }
        await context.sync();

        const items = paragraphs.items;
        const normOrig = normalizeWs(originalText);

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let targetParagraph: any = null;
        let resolvedWordIndex = -1;
        let resolvedBy:
          | 'exactUnique'
          | 'exactAmbiguousUsedHint'
          | 'exactAmbiguousFirst'
          | 'containsUsedHint'
          | 'containsUnique'
          | 'containsShortestOfMany'
          | 'prefixMatch'
          | 'indexFallback' = 'indexFallback';

        const exactIndices: number[] = [];
        for (let pi = 0; pi < items.length; pi++) {
          if (normalizeWs(items[pi].text || '') === normOrig) exactIndices.push(pi);
        }

        if (exactIndices.length === 1) {
          resolvedWordIndex = exactIndices[0];
          targetParagraph = items[resolvedWordIndex];
          resolvedBy = 'exactUnique';
        } else if (exactIndices.length > 1) {
          if (paragraphIndex != null && exactIndices.includes(paragraphIndex)) {
            resolvedWordIndex = paragraphIndex;
            targetParagraph = items[paragraphIndex];
            resolvedBy = 'exactAmbiguousUsedHint';
          } else {
            resolvedWordIndex = exactIndices[0];
            targetParagraph = items[exactIndices[0]];
            resolvedBy = 'exactAmbiguousFirst';
          }
        } else {
          const contains: number[] = [];
          if (normOrig.length >= MIN_SNIPPET_LEN_FOR_CONTAINS) {
            for (let pi = 0; pi < items.length; pi++) {
              if (normalizeWs(items[pi].text || '').includes(normOrig)) contains.push(pi);
            }
          }
          if (
            paragraphIndex != null &&
            paragraphIndex >= 0 &&
            paragraphIndex < items.length &&
            contains.includes(paragraphIndex)
          ) {
            resolvedWordIndex = paragraphIndex;
            targetParagraph = items[paragraphIndex];
            resolvedBy = 'containsUsedHint';
          } else if (contains.length === 1) {
            resolvedWordIndex = contains[0];
            targetParagraph = items[contains[0]];
            resolvedBy = 'containsUnique';
          } else if (contains.length > 1) {
            const sorted = [...contains].sort(
              (a, b) =>
                normalizeWs(items[a].text || '').length - normalizeWs(items[b].text || '').length,
            );
            resolvedWordIndex = sorted[0];
            targetParagraph = items[sorted[0]];
            resolvedBy = 'containsShortestOfMany';
          } else {
            // Prefix fallback: find a paragraph that STARTS WITH the first 120 chars of originalText.
            // This handles the case where a previous issue in the same apply round already modified
            // the paragraph body (so the full original no longer appears verbatim), but the opening
            // of the paragraph hasn't changed.
            const PREFIX_LEN = 120;
            const normPrefix = normOrig.slice(0, PREFIX_LEN);
            if (normPrefix.length >= MIN_SNIPPET_LEN_FOR_CONTAINS) {
              const prefixMatches: number[] = [];
              for (let pi = 0; pi < items.length; pi++) {
                if (normalizeWs(items[pi].text || '').startsWith(normPrefix)) prefixMatches.push(pi);
              }
              if (prefixMatches.length === 1) {
                resolvedWordIndex = prefixMatches[0];
                targetParagraph = items[prefixMatches[0]];
                resolvedBy = 'prefixMatch';
              } else if (prefixMatches.length > 1 && paragraphIndex != null && prefixMatches.includes(paragraphIndex)) {
                resolvedWordIndex = paragraphIndex;
                targetParagraph = items[paragraphIndex];
                resolvedBy = 'prefixMatch';
              } else if (prefixMatches.length > 1) {
                logError('[RedlineInserter] incremental apply: ambiguous prefix match');
                return { applied: false };
              }
            }

            if (!targetParagraph && paragraphIndex != null && paragraphIndex >= 0 && paragraphIndex < items.length) {
              resolvedWordIndex = paragraphIndex;
              targetParagraph = items[paragraphIndex];
              resolvedBy = 'indexFallback';
            }
          }
        }

        if (!targetParagraph) {
          logError('[RedlineInserter] incremental apply: paragraph not resolved', {
            paragraphIndexHint: paragraphIndex ?? null,
            paragraphCount: items.length,
            originalSnippet: snippet(originalText),
          });
          throw new Error('Could not find paragraph. Please select it and try again.');
        }

        const apiPlainText = (targetParagraph.text || '').trim();
        const actualParaTextFromApi = normalizeWs(apiPlainText);
        const actualParaTextFromApiStripped = normalizeWs(
          TrackChangesParser.stripRefFieldNoise(apiPlainText),
        );

        let acceptedRawFromParagraphOoxml: string | null = null;
        let ooxmlPackageLen = 0;
        let ooxmlHasFieldMarkers = false;
        let ooxmlParagraphXmlLen = 0;
        let ooxmlExtractError: string | null = null;
        // Capture the single <w:p> OOXML for the undo path.  We MUST use
        // singleParagraphXmlFromPackage here (not paragraphOrBodyXmlFromOoxmlPackage)
        // so we never store a <w:body> wrapper — see applySubmitAsIsViaInsertText for
        // the same rationale.
        let originalParagraphOoxmlForRevert: string | null = null;
        let originalParagraphOoxmlPackageForRevert: string | null = null;
        try {
          const ooxmlResult = (targetParagraph as any).getOoxml();
          await context.sync();
          const pkg = ooxmlResult?.value;
          if (typeof pkg === 'string' && pkg.length > 0) {
            ooxmlPackageLen = pkg.length;
            ooxmlHasFieldMarkers =
              /<w:fldChar\b/i.test(pkg) ||
              /<w:instrText\b/i.test(pkg) ||
              /\bREF\s+/i.test(pkg);
            const pXml = DocumentReader.paragraphOrBodyXmlFromOoxmlPackage(pkg);
            ooxmlParagraphXmlLen = pXml?.length ?? 0;
            if (pXml?.trim()) {
              acceptedRawFromParagraphOoxml = TrackChangesParser.extractAcceptedText(pXml);
            }
            // For undo path A (preferred on Win32): the FULL pkg:package Word produced,
            // surgically trimmed so <w:body> contains exactly the target <w:p>. Round-tripping
            // Word's own output into Range.insertOoxml restores the paragraph faithfully —
            // including pre-existing tracked changes, style references, numbering, and run
            // properties — without manually reconstructing OOXML. The minimal hand-crafted
            // package produced by OoxmlPackageWrapper.wrapParagraphInPackage is rejected by
            // Win32 insertOoxml for many real paragraphs (GeneralException) because it lacks
            // namespace declarations / package parts Win32 requires.
            const prunedPackage = OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(
              pkg,
              apiPlainText,
            );
            if (prunedPackage && prunedPackage.length > 0) {
              originalParagraphOoxmlPackageForRevert = prunedPackage;
            }
            // For undo path B (fallback): single <w:p> only so wrapParagraphInPackage doesn't
            // produce nested <w:body><w:body> on Win32 (where paragraph.getOoxml() can return
            // a <w:body> wrapping the target paragraph plus trailing section context).
            const singlePXml = DocumentReader.singleParagraphXmlFromPackage(pkg, apiPlainText);
            if (singlePXml?.trim()) {
              originalParagraphOoxmlForRevert = singlePXml;
            }
          }
        } catch (e) {
          ooxmlExtractError = e instanceof Error ? e.message : String(e);
        }

        const actualParaTextFromOoxml = acceptedRawFromParagraphOoxml
          ? normalizeWs(acceptedRawFromParagraphOoxml)
          : '';

        const baselineMismatchOoxmlVsApi =
          actualParaTextFromOoxml.length > 0 && actualParaTextFromApi.length > 0
            ? firstTextMismatchInfo(actualParaTextFromOoxml, actualParaTextFromApi)
            : null;

        // Range.search runs against Word's *visible* paragraph.text. When OOXML accepted
        // text diverges (counterparty w:ins boundaries, field chars like \u0005, extra
        // struck-through surface text), charPos/occIdx computed on OOXML will select the
        // wrong match — e.g. occIdx=1 for " היזם" with only 1 Word hit → wrong "כנגד היזם".
        const apiBaselineRequired =
          actualParaTextFromApi.length > 0 &&
          (baselineMismatchOoxmlVsApi != null ||
            (actualParaTextFromOoxml.length > 0 &&
              actualParaTextFromApi.length !== actualParaTextFromOoxml.length));

        const actualParaText = apiBaselineRequired
          ? actualParaTextFromApi
          : actualParaTextFromOoxml.length > 0
            ? actualParaTextFromOoxml
            : actualParaTextFromApi;
        const opsBaselineSource: 'api' | 'ooxml' = apiBaselineRequired
          ? 'api'
          : actualParaTextFromOoxml.length > 0
            ? 'ooxml'
            : 'api';

        const passesIndexGuardApi =
          actualParaTextFromApi.includes(normOrig) || actualParaTextFromApiStripped.includes(normOrig);
        const passesIndexGuardOoxml =
          actualParaTextFromOoxml.length > 0 && actualParaTextFromOoxml.includes(normOrig);

        const baselineMismatchApi = baselineMismatchOoxmlVsApi;
        const opCharPositions = [36, 206, 207, 298, 299, 300].filter(
          (p) => p < actualParaText.length,
        );

        log('[RedlineInserter] incremental apply: paragraph resolved', {
          resolvedWordIndex,
          resolvedBy,
          paragraphIndexHint: paragraphIndex ?? null,
          paragraphCount: items.length,
          originalNormLen: normOrig.length,
          apiTextLen: actualParaTextFromApi.length,
          apiStrippedLen: actualParaTextFromApiStripped.length,
          ooxmlAcceptedLen: actualParaTextFromOoxml.length,
          usedOoxmlForOps: opsBaselineSource === 'ooxml',
          opsBaselineSource,
          apiBaselineRequired,
          ooxmlPackageLen,
          ooxmlParagraphXmlLen,
          ooxmlHasFieldMarkers,
          ooxmlExtractError,
          passesIndexGuardApi,
          passesIndexGuardOoxml,
          actualParaLenForOps: actualParaText.length,
          actualSnippet: snippet(actualParaText),
          apiSnippet: snippet(actualParaTextFromApi),
          newTextSnippet: snippet(normalizeWs(newText)),
          baselineMismatchActualVsApi: baselineMismatchApi,
        });

        log('[RedlineInserter] incremental apply: TEXT_BASELINE_AT_OP_POSITIONS', {
          actualParaText: textContextAtPositions(actualParaText, opCharPositions),
          apiParaText: textContextAtPositions(actualParaTextFromApi, opCharPositions),
          normOrig: textContextAtPositions(normOrig, opCharPositions),
        });

        // Audit key search needles that historically mis-match between baselines.
        for (const probe of ['כנגד היזם', 'היזם', 'בשליטת היזם', 'מהיזמים', 'מבעלי השליטה ביזם']) {
          log('[RedlineInserter] incremental apply: NEEDLE_OCCURRENCE_PROBE', {
            probe,
            ...RedlineInserter.auditSearchAnchor(probe, actualParaText, actualParaTextFromApi, 0),
          });
        }

        let effectiveTrackChangesHtml = trackChangesHtml;
        let usedLiveParagraphWordDiff = false;

        const newNorm = normalizeWs(newText);
        const indexGuardFailed =
          resolvedBy === 'indexFallback' && !passesIndexGuardApi && !passesIndexGuardOoxml;
        const shouldUseLiveWordDiff = indexGuardFailed;

        if (shouldUseLiveWordDiff) {
          const mismatchApi = firstTextMismatchInfo(normOrig, actualParaTextFromApi);
          const mismatchOoxml =
            actualParaTextFromOoxml.length > 0
              ? firstTextMismatchInfo(normOrig, actualParaTextFromOoxml)
              : null;
          log(
            '[RedlineInserter] incremental apply: live word-diff recovery',
            {
              reason: 'index_guard_failed',
              paragraphIndexHint: paragraphIndex ?? null,
              resolvedWordIndex,
              opsBaselineSource,
              originalNormLen: normOrig.length,
              apiTextLen: actualParaTextFromApi.length,
              ooxmlAcceptedLen: actualParaTextFromOoxml.length,
              baselineMismatchOoxmlVsApi,
              ooxmlHasFieldMarkers,
              mismatchVsApiText: mismatchApi,
              mismatchVsOoxmlAccepted: mismatchOoxml,
              originalSnippet: snippet(normOrig),
              apiSnippet: snippet(actualParaTextFromApi),
            },
          );

          const liveBaseline = actualParaText;
          if (!liveBaseline || !newNorm) {
            logError('[RedlineInserter] incremental apply: cannot recover — empty live or target text', {
              resolvedWordIndex,
            });
            return { applied: false };
          }
          if (liveBaseline === newNorm) {
            log('[RedlineInserter] incremental apply: live paragraph already matches target — nothing to apply');
            return { applied: true };
          }
          effectiveTrackChangesHtml = RedlineInserter.buildTrackChangesHtmlFromWordDiff(
            liveBaseline,
            newNorm,
          );
          usedLiveParagraphWordDiff = true;
          log('[RedlineInserter] incremental apply: using synthetic trackChangesHtml from live paragraph vs target (word diff)', {
            liveBaselineLen: liveBaseline.length,
            liveBaselineSource: opsBaselineSource,
            newNormLen: newNorm.length,
            syntheticHtmlLen: effectiveTrackChangesHtml.length,
          });
        }

        let incLens = RedlineInserter.incrementalOpsSearchLens(effectiveTrackChangesHtml);

        // Recovery: preview HTML can coalesce a long trailing deletion into one 400+ char search
        // anchor (Word.js rejects >255). Re-diff live paragraph vs target into word-level ops.
        if (incLens.anyOpExceedsWordJsSearchLimit && !usedLiveParagraphWordDiff) {
          const liveBaseline = actualParaText;
          if (liveBaseline && newNorm && liveBaseline !== newNorm) {
            effectiveTrackChangesHtml = RedlineInserter.buildTrackChangesHtmlFromWordDiff(
              liveBaseline,
              newNorm,
            );
            usedLiveParagraphWordDiff = true;
            log(
              '[RedlineInserter] incremental apply: recovered via live word-diff (search limit)',
              {
                liveBaselineLen: liveBaseline.length,
                newNormLen: newNorm.length,
                syntheticHtmlLen: effectiveTrackChangesHtml.length,
              },
            );
          }
        }

        const recoveredOpsSummary =
          RedlineInserter.summarizeTrackChangesHtmlOps(effectiveTrackChangesHtml);
        incLens = RedlineInserter.incrementalOpsSearchLens(effectiveTrackChangesHtml);
        const canUseIncremental = RedlineInserter.canRunIncrementalWithOpsSummary(
          recoveredOpsSummary,
          actualParaText.length,
        );
        const appendableTrailingInserts =
          recoveredOpsSummary.anyInsertMissingSearchAnchor && canUseIncremental;
        log('[RedlineInserter] incremental apply: parsed ops summary', {
          usedLiveParagraphWordDiff,
          effectiveTrackChangesHtmlLen: effectiveTrackChangesHtml.length,
          actualParaLenForOps: actualParaText.length,
          insertAtParagraphEnd:
            recoveredOpsSummary.trailingInsertOnly &&
            recoveredOpsSummary.maxCharPos >= actualParaText.length - 1,
          appendableTrailingInserts,
          ...recoveredOpsSummary,
        });
        log('[RedlineInserter] incremental apply: mode decision', {
          resolvedBy,
          usedLiveParagraphWordDiff,
          opCount: incLens.opCount,
          anyOpExceedsWordJsSearchLimit: incLens.anyOpExceedsWordJsSearchLimit,
          anyInsertMissingSearchAnchor: recoveredOpsSummary.anyInsertMissingSearchAnchor,
          trailingInsertOnly: recoveredOpsSummary.trailingInsertOnly,
          appendableTrailingInserts,
          canUseIncremental,
          willSplitOversizedOps: incLens.anyOpExceedsWordJsSearchLimit,
        });

        if (!canUseIncremental) {
          logError('[RedlineInserter] incremental apply: cannot run — no ops or missing insert anchor', {
            opCount: incLens.opCount,
            anyInsertMissingSearchAnchor: recoveredOpsSummary.anyInsertMissingSearchAnchor,
          });
          return { applied: false };
        }

        const rawOps = RedlineInserter.parseHtmlOps(effectiveTrackChangesHtml);
        // Coalesce on the baseline that matches op charPos. Live word-diff ops are positioned
        // against actualParaText; preview HTML ops use normOrig (client-request accepted text).
        const coalesceBaseline =
          usedLiveParagraphWordDiff || normOrig.length === 0 ? actualParaText : normOrig;
        let coalescedOps = RedlineInserter.coalesceParsedOps(rawOps, coalesceBaseline);
        if (apiBaselineRequired && !usedLiveParagraphWordDiff) {
          coalescedOps = RedlineInserter.remapOpsToApiBaseline(
            coalescedOps,
            normOrig,
            actualParaTextFromApi,
          );
          log('[RedlineInserter] incremental apply: remapped coalesced ops to API text', {
            ooxmlBaselineLen: normOrig.length,
            apiTextLen: actualParaTextFromApi.length,
            remappedOps: coalescedOps.map((op) => ({
              kind: op.kind,
              charPos: op.charPos,
              ...(op.kind === 'replace'
                ? {
                    deleteSnippet: snippet(op.deleteText),
                    insertSnippet: snippet(op.insertText),
                  }
                : {}),
              ...(op.kind === 'delete' ? { deleteSnippet: snippet(op.text) } : {}),
            })),
          });
        }

        coalescedOps = RedlineInserter.splitOversizedParsedOps(coalescedOps);
        if (!RedlineInserter.parsedOpsFitWordSearchLimit(coalescedOps)) {
          logError('[RedlineInserter] incremental apply: ops still exceed Word search limit after split', {
            opCount: coalescedOps.length,
          });
          return { applied: false };
        }

        if (coalescedOps.length === 0) {
          log('[RedlineInserter] incremental apply: no parsed ops — nothing to apply');
          return { applied: true };
        }

        log('[RedlineInserter] incremental apply: coalesced ops', {
          rawOpCount: rawOps.length,
          coalescedOpCount: coalescedOps.length,
          rawOps: rawOps.map((op) => ({
            kind: op.kind,
            charPos: op.charPos,
            ...(op.kind === 'replace'
              ? { deleteSnippet: snippet(op.deleteText), insertSnippet: snippet(op.insertText) }
              : {}),
            ...(op.kind === 'delete' ? { deleteSnippet: snippet(op.text) } : {}),
            ...(op.kind === 'insert' ? { insertSnippet: snippet(op.text) } : {}),
          })),
          coalesced: coalescedOps.map((op) => ({
            kind: op.kind,
            charPos: op.charPos,
            ...(op.kind === 'replace'
              ? {
                  deleteTextLen: op.deleteText.length,
                  insertTextLen: op.insertText.length,
                  deleteSnippet: snippet(op.deleteText),
                  insertSnippet: snippet(op.insertText),
                  sliceAtCharPos: actualParaText.slice(op.charPos, op.charPos + op.deleteText.length),
                }
              : {}),
            ...(op.kind === 'delete'
              ? { deleteTextLen: op.text.length, deleteSnippet: snippet(op.text) }
              : {}),
          })),
        });

        targetParagraph.getRange('Whole').select();
        await context.sync();

        const beforeFingerprints =
          await RedlineInserter.collectTrackedChangeFingerprints(targetParagraph, context);

        // Right-to-left so earlier charPos values stay valid against the original baseline text.
        const pass1 = coalescedOps.sort((a, b) => b.charPos - a.charPos);
        let failed = 0;
        const applyStartedAtMs = Date.now();

        for (let opIndex = 0; opIndex < pass1.length; opIndex++) {
          const op = pass1[opIndex];
          let searchText = '';
          // Effective position used for `countOccurrencesBefore`; for replace ops this can
          // differ from op.charPos when coalesceParsedOps shifts positions near counterparty
          // track-change markup. `resolveReplaceSearchText` relocates to where the search
          // text actually lives so disambiguation stays correct.
          let effectiveCharPos = op.charPos;
          let charPosAdjustment = 0;
          if (op.kind === 'replace') {
            if (apiBaselineRequired) {
              // deleteText/charPos already aligned to Word visible text by remapOpsToApiBaseline.
              searchText = stripWordSearchNoise(op.deleteText) || op.deleteText;
              effectiveCharPos = op.charPos;
            } else {
              const resolved = RedlineInserter.resolveReplaceSearchText(
                actualParaText,
                op.charPos,
                op.deleteText,
              );
              searchText = resolved.searchText;
              effectiveCharPos = resolved.effectiveCharPos;
            }
            charPosAdjustment = effectiveCharPos - op.charPos;
          } else if (op.kind === 'delete') {
            searchText = RedlineInserter.resolveDeleteSearchText(
              actualParaText,
              op.charPos,
              op.text,
            );
            effectiveCharPos = op.charPos;
          } else {
            searchText = RedlineInserter.searchTextForOp(op);
          }

          const searchAnchorAudit =
            searchText.length > 0
              ? RedlineInserter.auditSearchAnchor(
                  searchText,
                  actualParaText,
                  actualParaTextFromApi,
                  effectiveCharPos,
                )
              : null;

          log('[RedlineInserter] incremental apply: op attempt', {
            opIndex,
            totalOps: pass1.length,
            applyOrder: 'right_to_left',
            kind: op.kind,
            charPos: op.charPos,
            effectiveCharPos,
            charPosAdjustment,
            searchTextLen: searchText.length,
            searchSnippet: snippet(searchText),
            searchAnchorAudit,
            contextAtEffectivePos: textContextAtPositions(actualParaText, [effectiveCharPos], 30),
            contextAtEffectivePosApi: textContextAtPositions(actualParaTextFromApi, [effectiveCharPos], 30),
            ambiguousShortAnchor:
              op.kind !== 'insert' &&
              searchText.length > 0 &&
              searchText.length < MIN_DELETE_SEARCH_ANCHOR_LEN,
            ...(op.kind === 'insert'
              ? {
                  insertTextLen: op.text.length,
                  insertSnippet: snippet(op.text),
                  suffixWords: op.suffixWords,
                  atParagraphEnd: op.charPos >= actualParaText.length - 1,
                }
              : {}),
            ...(op.kind === 'replace'
              ? {
                  deleteTextLen: op.deleteText.length,
                  insertTextLen: op.insertText.length,
                  deleteTextSnippet: snippet(op.deleteText),
                  insertTextSnippet: snippet(op.insertText),
                }
              : {}),
            ...(op.kind === 'delete' ? { deleteTextSnippet: snippet(op.text) } : {}),
          });

          if (searchText === '') {
            // Trailing-insert case: an `insert` op at or past the paragraph end has no
            // following text to anchor on. Append directly to the paragraph's end range
            // — Word will record this as a tracked insertion when Track Changes is on.
            const isTrailingInsert =
              op.kind === 'insert' &&
              op.suffixWords.length === 0 &&
              op.charPos >= actualParaText.length - 1;

            if (isTrailingInsert) {
              try {
                const insertText = prepareBidiTextForWordInsert(op.text.trim());
                if (!insertText) {
                  log('[RedlineInserter] incremental apply: trailing insert is empty after trim, skipping', {
                    opIndex,
                    charPos: op.charPos,
                  });
                  continue;
                }
                const endRange = targetParagraph.getRange('End');
                endRange.insertText(' ' + insertText, Word.InsertLocation.after);
                await context.sync();
                log('[RedlineInserter] incremental apply: trailing insert appended at paragraph end', {
                  opIndex,
                  charPos: op.charPos,
                  insertLen: insertText.length,
                  insertSnippet: snippet(insertText),
                });
                log('[TrackChangeAuthor] Word trailing insert via insertText', {
                  opIndex,
                  note: 'Word attributes insertText to the current Word user, not w:author from preview OOXML',
                  insertSnippet: snippet(insertText),
                });
                continue;
              } catch (appendErr) {
                failed++;
                logError('[RedlineInserter] incremental apply: trailing insert append failed', {
                  opIndex,
                  charPos: op.charPos,
                  insertSnippet: snippet(op.text),
                  error: appendErr,
                });
                continue;
              }
            }

            failed++;
            logError('[RedlineInserter] incremental apply: op skipped — no search anchor', {
              opIndex,
              kind: op.kind,
              charPos: op.charPos,
              actualParaLen: actualParaText.length,
              atParagraphEnd: op.charPos >= actualParaText.length - 1,
              ...(op.kind === 'insert'
                ? {
                    suffixWords: op.suffixWords,
                    insertSnippet: snippet(op.text),
                    likelyCause:
                      op.suffixWords.length === 0
                        ? 'trailing_insert_no_following_text_for_suffix_anchor'
                        : 'insert_with_empty_suffix_word',
                  }
                : {}),
            });
            continue;
          }

          let target: Word.Range | null = null;
          let lastResultCount = 0;
          let matchedAnchor = searchText;

          // ── Step A: Hierarchical context-anchored search (preferred for replace/delete) ──
          //
          // For replace/delete we first try to pin the location with a multi-word parent anchor
          // that is unique in `actualParaText`, then narrow to the inner target inside that
          // parent range. This is more reliable than `occIdx`-on-flat-search because Word's
          // visible text includes counterparty track-change deletions: short anchors like " היזם"
          // can match inside struck-through text, shifting `occIdx` so the edit lands in the
          // wrong place. A longer multi-word anchor almost never collides with deletion text.
          if (op.kind === 'replace' || op.kind === 'delete') {
            const targetForContext = op.kind === 'replace' ? op.deleteText : op.text;
            const uniqueContext = RedlineInserter.findUniqueContextAnchor(
              actualParaText,
              op.charPos,
              targetForContext,
            );
            if (!uniqueContext) {
              log('[RedlineInserter] incremental apply: context anchor build failed', {
                opIndex,
                kind: op.kind,
                charPos: op.charPos,
                targetSnippet: snippet(targetForContext),
              });
            } else if (uniqueContext.anchor.length > WORD_JS_MAX_SEARCH_STRING_LENGTH) {
              log('[RedlineInserter] incremental apply: context anchor too long for Word.search', {
                opIndex,
                anchorLen: uniqueContext.anchor.length,
                anchorSnippet: snippet(uniqueContext.anchor),
              });
            }

            if (
              uniqueContext &&
              uniqueContext.anchor.length <= WORD_JS_MAX_SEARCH_STRING_LENGTH
            ) {
              const searchableParent = stripWordSearchNoise(uniqueContext.anchor);
              const searchableInner = stripWordSearchNoise(uniqueContext.innerText);
              if (!searchableParent || !searchableInner) {
                log('[RedlineInserter] incremental apply: context anchor empty after noise strip', {
                  opIndex,
                  kind: op.kind,
                  parentSnippet: snippet(uniqueContext.anchor),
                  innerSnippet: snippet(uniqueContext.innerText),
                });
              }
              const parentSearch = (targetParagraph.getRange('Whole') as any).search(
                searchableParent || uniqueContext.anchor,
                { matchCase: false },
              );
              parentSearch.load('items');
              await context.sync();
              const parentMatches = parentSearch.items?.length ?? 0;
              if (parentMatches >= 1) {
                // Anchor is unique in actualParaText. In Word's visible text it might match more
                // than once only if HERZOG deleted this exact multi-word phrase — extremely rare.
                // Use the first match; if there are multiple, fall back below.
                const parentRange = parentSearch.items[0];
                const innerSearch = (parentRange as any).search(
                  searchableInner || uniqueContext.innerText,
                  { matchCase: false },
                );
                innerSearch.load('items');
                await context.sync();
                const innerMatches = innerSearch.items?.length ?? 0;
                if (innerMatches === 1) {
                  target = innerSearch.items[0];
                  matchedAnchor = uniqueContext.innerText;
                  log('[RedlineInserter] incremental apply: context-anchored match', {
                    opIndex,
                    kind: op.kind,
                    charPos: op.charPos,
                    parentAnchorSnippet: snippet(uniqueContext.anchor),
                    parentAnchorLen: uniqueContext.anchor.length,
                    parentMatches,
                    innerSnippet: snippet(uniqueContext.innerText),
                    expandedBy: uniqueContext.expandedBy,
                  });
                } else {
                  log('[RedlineInserter] incremental apply: context-anchored search ambiguous, falling back', {
                    opIndex,
                    kind: op.kind,
                    parentAnchorSnippet: snippet(uniqueContext.anchor),
                    parentMatches,
                    innerMatches,
                    innerSnippet: snippet(uniqueContext.innerText),
                  });
                }
              } else {
                log('[RedlineInserter] incremental apply: context anchor not found in Word, falling back', {
                  opIndex,
                  kind: op.kind,
                  parentAnchorSnippet: snippet(uniqueContext.anchor),
                  parentAnchorLen: uniqueContext.anchor.length,
                });
              }
            }
          }

          // ── Step B: Flat search with progressive fallbacks (used for insert, and as fallback) ──
          const searchAttempts: Array<{ anchor: string; charPos: number; label: string }> = [
            {
              anchor: stripWordSearchNoise(searchText) || searchText,
              charPos: effectiveCharPos,
              label: 'primary',
            },
          ];
          if (op.kind === 'replace') {
            const fallbacks = RedlineInserter.buildReplaceFallbackAnchors(
              actualParaText,
              effectiveCharPos,
              op.deleteText,
              searchText,
            );
            for (const fallback of fallbacks) {
              const searchableFallback = stripWordSearchNoise(fallback) || fallback;
              if (searchableFallback.length > WORD_JS_MAX_SEARCH_STRING_LENGTH) continue;
              searchAttempts.push({
                anchor: searchableFallback,
                charPos: effectiveCharPos,
                label: 'replace_fallback',
              });
            }
          }

          if (!target) {
            for (const attempt of searchAttempts) {
              if (!attempt.anchor) continue;
              if (attempt.anchor.length > WORD_JS_MAX_SEARCH_STRING_LENGTH) {
                logWarn('[RedlineInserter] incremental apply: flat search anchor too long — skip', {
                  opIndex,
                  kind: op.kind,
                  anchorLen: attempt.anchor.length,
                  label: attempt.label,
                });
                continue;
              }
              const occIdxAttempt = RedlineInserter.countOccurrencesBefore(
                stripWordSearchNoise(actualParaText),
                attempt.anchor,
                stripWordSearchNoise(actualParaText.slice(0, attempt.charPos)).length,
              );
              const freshRangeAttempt = targetParagraph.getRange('Whole');
              const resultsAttempt = (freshRangeAttempt as any).search(attempt.anchor, {
                matchCase: false,
              });
              resultsAttempt.load('items');
              await context.sync();
              const resultCountAttempt = resultsAttempt.items?.length ?? 0;
              lastResultCount = resultCountAttempt;

              const matchTexts: string[] = [];
              if (resultCountAttempt > 0) {
                for (const item of resultsAttempt.items) {
                  (item as Word.Range).load('text');
                }
                await context.sync();
                for (let mi = 0; mi < resultsAttempt.items.length; mi++) {
                  matchTexts.push((resultsAttempt.items[mi] as Word.Range).text || '');
                }
              }

              log('[RedlineInserter] incremental apply: FLAT_SEARCH_ATTEMPT', {
                opIndex,
                kind: op.kind,
                label: attempt.label,
                anchorSnippet: snippet(attempt.anchor),
                anchorLen: attempt.anchor.length,
                charPos: attempt.charPos,
                occIdxAttempt,
                resultCount: resultCountAttempt,
                matchTexts: matchTexts.map((t, i) => ({
                  matchIndex: i,
                  textSnippet: snippet(t),
                  selected: i === occIdxAttempt || (occIdxAttempt >= resultCountAttempt && i === resultCountAttempt - 1),
                })),
                anchorAudit: RedlineInserter.auditSearchAnchor(
                  attempt.anchor,
                  actualParaText,
                  actualParaTextFromApi,
                  attempt.charPos,
                ),
              });

              if (resultCountAttempt > 0 && occIdxAttempt < resultCountAttempt) {
                target = resultsAttempt.items[occIdxAttempt];
                matchedAnchor = attempt.anchor;
                log('[RedlineInserter] incremental apply: FLAT_SEARCH_SELECTED', {
                  opIndex,
                  kind: op.kind,
                  label: attempt.label,
                  selectedMatchIndex: occIdxAttempt,
                  selectedMatchText: matchTexts[occIdxAttempt] ?? null,
                  occIdxAttempt,
                  resultCount: resultCountAttempt,
                  warning: occIdxAttempt > 0 ? 'non-zero occIdx — verify match is correct region' : null,
                });
                if (attempt.label !== 'primary') {
                  log('[RedlineInserter] incremental apply: flat fallback anchor matched', {
                    opIndex,
                    kind: op.kind,
                    charPos: op.charPos,
                    primarySnippet: snippet(searchText),
                    fallbackSnippet: snippet(attempt.anchor),
                    resultCount: resultCountAttempt,
                    occIdx: occIdxAttempt,
                  });
                }
                break;
              }
              if (resultCountAttempt > 0 && occIdxAttempt >= resultCountAttempt) {
                logWarn('[RedlineInserter] incremental apply: FLAT_SEARCH_occIdx_out_of_range — skip attempt', {
                  opIndex,
                  kind: op.kind,
                  label: attempt.label,
                  anchorSnippet: snippet(attempt.anchor),
                  occIdxAttempt,
                  resultCount: resultCountAttempt,
                  matchTexts: matchTexts.map((t, i) => ({ i, snippet: snippet(t) })),
                  reason:
                    'occIdx computed on ops baseline exceeds Word search hits — would pick wrong location',
                });
              }
            }
          }

          try {
            if (!target) {
              logError('[RedlineInserter] incremental apply: search target not found', {
                kind: op.kind,
                charPos: op.charPos,
                effectiveCharPos,
                attemptsTried: searchAttempts.length,
                lastResultCount,
                primarySnippet: snippet(searchText),
                fallbackAnchors: searchAttempts.slice(1).map((a) => snippet(a.anchor)),
              });
              failed++;
              continue;
            }

            if (op.kind === 'replace') {
              // To produce OOXML order <w:del>old</w:del><w:ins>new</w:ins> (matching the
              // preview), insert AFTER the original range first, then delete it. Using
              // InsertLocation.replace would emit <w:ins>new</w:ins><w:del>old</w:del>
              // (insertion shown before deletion in Word), which mismatches the card preview.
              //
              // Whitespace must be derived from the *matched anchor* (which is what we'll
              // actually delete), not from op.deleteText. A fallback retry may have matched
              // a trimmed anchor — in that case op.deleteText has spaces the target doesn't,
              // and using op.deleteText for spacing would leave extra/missing spaces.
              const leading = matchedAnchor.match(/^\s+/)?.[0] || '';
              const trailing = matchedAnchor.match(/\s+$/)?.[0] || '';
              const insertCore = prepareBidiTextForWordInsert(op.insertText.replace(/\s+/g, ' ').trim());
              const insertWithBoundary = `${leading}${insertCore}${trailing}`;
              log('[RedlineInserter] incremental apply: replace via insert-after + delete', {
                charPos: op.charPos,
                matchedAnchorLen: matchedAnchor.length,
                deleteLen: op.deleteText.length,
                insertLen: insertWithBoundary.length,
                leadingSpace: leading.length,
                trailingSpace: trailing.length,
                insertSnippet: snippet(insertWithBoundary),
              });
              target.insertText(insertWithBoundary, Word.InsertLocation.after);
              await context.sync();
              (target as any).delete();
            } else if (op.kind === 'delete') {
              (target as any).delete();
            } else {
              // Preserve leading/trailing spaces from preview HTML. trim()+forced trailing
              // space caused "יתווסףסעיף" and "חדש :" mismatches vs the card preview.
              target.insertText(prepareBidiTextForWordInsert(op.text), Word.InsertLocation.before);
            }

            await context.sync();

            targetParagraph.load('text');
            await context.sync();
            const paraTextAfterOp = normalizeWs(targetParagraph.text || '');
            log('[RedlineInserter] incremental apply: POST_OP_PARAGRAPH_SNAPSHOT', {
              opIndex,
              kind: op.kind,
              charPos: op.charPos,
              matchedAnchorSnippet: snippet(matchedAnchor),
              paraTextLen: paraTextAfterOp.length,
              paraTextSnippet: paraTextAfterOp.slice(0, 150),
              deltaLenFromBaseline: paraTextAfterOp.length - actualParaTextFromApi.length,
            });
          } catch (opErr) {
            failed++;
            logError('[RedlineInserter] incremental apply: op failed', {
              kind: op.kind,
              charPos: op.charPos,
              searchSnippet: snippet(searchText),
              error: opErr,
            });
          }
        }

        targetParagraph.getRange('Whole').select();
        await context.sync();

        targetParagraph.load('text');
        await context.sync();
        const finalApiText = normalizeWs(targetParagraph.text || '');
        let finalOoxmlAccepted = '';
        try {
          const finalOoxmlResult = (targetParagraph as any).getOoxml();
          await context.sync();
          const finalPkg = finalOoxmlResult?.value;
          if (typeof finalPkg === 'string' && finalPkg.length > 0) {
            const finalPXml = DocumentReader.paragraphOrBodyXmlFromOoxmlPackage(finalPkg);
            if (finalPXml?.trim()) {
              finalOoxmlAccepted = normalizeWs(TrackChangesParser.extractAcceptedText(finalPXml));
            }
          }
        } catch {
          // ignore
        }

        const targetNorm = normalizeWs(newText);
        log('[RedlineInserter] incremental apply: POST_APPLY_VERIFY', {
          totalOps: pass1.length,
          failedOps: failed,
          ok: failed === 0,
          resolvedBy,
          resolvedWordIndex,
          finalApiTextLen: finalApiText.length,
          finalOoxmlAcceptedLen: finalOoxmlAccepted.length,
          targetNewTextLen: targetNorm.length,
          finalApiMatchesTarget: finalApiText === targetNorm,
          finalOoxmlMatchesTarget: finalOoxmlAccepted === targetNorm,
          finalApiSnippet: finalApiText.slice(0, 150),
          targetSnippet: targetNorm.slice(0, 150),
          mismatchFinalApiVsTarget: firstTextMismatchInfo(finalApiText, targetNorm),
          mismatchFinalOoxmlVsTarget:
            finalOoxmlAccepted.length > 0
              ? firstTextMismatchInfo(finalOoxmlAccepted, targetNorm)
              : null,
        });

        const applyFinishedAtMs = Date.now();
        const afterFingerprints =
          await RedlineInserter.collectTrackedChangeFingerprints(targetParagraph, context);
        const revertContext = buildSubmitRevertContext(
          applyStartedAtMs,
          applyFinishedAtMs,
          beforeFingerprints,
          afterFingerprints,
          resolvedWordIndex,
        );

        // Attach the pre-apply plain text and single-paragraph OOXML so the undo path
        // has a text-replacement fallback on Win32. On Win32, incremental-apply tracked
        // changes created via Range.insertText/delete get the pre-existing TC timestamp
        // from the document (e.g. May 2025) rather than the current JS Date.now(). The
        // apply-window check therefore never matches and tc.reject() cannot find our
        // changes. The text-replacement fallback (TC-off → insertText(originalText) →
        // TC-on) is the only reliable undo path in that case.
        revertContext.originalParagraphText = actualParaTextFromApi || apiPlainText;
        if (originalParagraphOoxmlForRevert) {
          revertContext.originalParagraphOoxml = originalParagraphOoxmlForRevert;
        }
        if (originalParagraphOoxmlPackageForRevert) {
          revertContext.originalParagraphOoxmlPackage = originalParagraphOoxmlPackageForRevert;
        }

        log('[RedlineInserter] incremental apply: finished', {
          totalOps: pass1.length,
          failedOps: failed,
          ok: failed === 0,
          resolvedBy,
          resolvedWordIndex,
          paragraphIndex: resolvedWordIndex,
          applyStartedAtMs,
          applyFinishedAtMs,
          addedFingerprints: revertContext.addedFingerprints.length,
          originalParagraphTextLen: revertContext.originalParagraphText?.length ?? 0,
          hasOriginalParagraphOoxml: revertContext.originalParagraphOoxml != null,
          hasOriginalParagraphOoxmlPackage: revertContext.originalParagraphOoxmlPackage != null,
        });
        return { applied: failed === 0, revertContext };
      });

      log('[RedlineInserter] incremental apply: returned', {
        applied: incrementalResult.applied,
        hasRevertContext: incrementalResult.revertContext != null,
        paragraphIndex: incrementalResult.revertContext?.paragraphIndex ?? null,
      });
      return incrementalResult;

    } catch (error) {
      // Top-level error handler - catches any errors not caught in individual operations
      logError('[RedlineInserter] incremental apply failed', error);
      throw error; // Re-throw to let caller handle
    }
  }

  /* ------ search helper (called inside Word.run) ------ */

  /**
   * Search for `searchText` inside `paraRange`.
   * If multiple matches, disambiguate using prefix and suffix context.
   * Returns the first (leftmost) correct match, or null.
   *
   * Disambiguation uses the paragraph’s actual text: for each candidate we read
   * the text before and after from the document and check word-by-word (any whitespace),
   * so spacing is derived from the document, not assumed.
   */

  /**
   * Parses trackChangesHtml into a list of change operations in document order.
   *
   * Each op carries a charPos (character offset in the paragraph's accepted text) that lets
   * countOccurrencesBefore pick the exact search result without prefix/suffix word matching.
   *
   * Consecutive new-del + new-ins segments with no intervening text are merged into a single
   * replace op, so Word records both the deletion and insertion in one operation.
   *
   * Accepted text = regular text + old insertions + new-deletion text.
   * Old deletions (track-change-deletion WITHOUT track-change-new) are excluded because they
   * are already tracked as deleted in Word and invisible to Range.search().
   */
  private static parseHtmlOps(html: string): ParsedHtmlOp[] {
    const body = new DOMParser().parseFromString(html, 'text/html').body;

    type Seg = { kind: 'text'; text: string } | { kind: 'new-del'; text: string } | { kind: 'new-ins'; text: string };

    const isNewDel = (el: Element) =>
      el.tagName === 'DEL' && el.classList.contains('track-change-deletion') && el.classList.contains('track-change-new');
    const isNewIns = (el: Element) =>
      el.tagName === 'INS' && el.classList.contains('track-change-insertion') && el.classList.contains('track-change-new');
    const isOldDel = (el: Element) =>
      el.tagName === 'DEL' && el.classList.contains('track-change-deletion') && !el.classList.contains('track-change-new');

    function collectText(node: Node): string {
      if (node.nodeType === Node.TEXT_NODE) return node.textContent || '';
      if (node.nodeType === Node.ELEMENT_NODE) {
        if (isOldDel(node as Element)) return '';
        return Array.from(node.childNodes).map(collectText).join('');
      }
      return '';
    }

    // Build flat segment list; whitespace-only text nodes are omitted.
    const segs: Seg[] = [];
    for (const node of Array.from(body.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        const t = node.textContent || '';
        if (t.trim()) segs.push({ kind: 'text', text: t });
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        const el = node as Element;
        if (isNewDel(el)) {
          segs.push({ kind: 'new-del', text: el.textContent || '' });
        } else if (isNewIns(el)) {
          segs.push({ kind: 'new-ins', text: el.textContent || '' });
        } else if (!isOldDel(el)) {
          const t = collectText(el);
          if (t.trim()) segs.push({ kind: 'text', text: t });
        }
        // else: old-del → skip
      }
    }

    const ops: ParsedHtmlOp[] = [];
    let charPos = 0;
    let i = 0;

    while (i < segs.length) {
      const seg = segs[i];
      if (seg.kind === 'text') {
        charPos += seg.text.length;
        i++;
      } else if (seg.kind === 'new-del') {
        const pos = charPos;
        charPos += seg.text.length;
        // Pair with an immediately following new-ins → single replace op.
        if (i + 1 < segs.length && segs[i + 1].kind === 'new-ins') {
          const insText = (segs[i + 1] as { kind: 'new-ins'; text: string }).text;
          ops.push({ kind: 'replace', deleteText: seg.text, insertText: insText, charPos: pos });
          i += 2;
        } else {
          ops.push({ kind: 'delete', text: seg.text, charPos: pos });
          i++;
        }
      } else {
        // new-ins: suffix comes only from subsequent text segments (stable after deletions).
        //
        // IMPORTANT: preserve the leading whitespace of the FIRST text segment after this
        // insert. In the OOXML accepted model, that whitespace belongs to the *following*
        // text block (e.g. ` הקונה`), not to a gap between the insertion and the suffix.
        // If we strip it via `.trim()`, Word.search anchors on the bare word (`הקונה`) at
        // a position past the space, and `insertText(text, before)` then lands the
        // insertion AFTER the original space — producing an extra space between the new
        // text and the preceding content (e.g. `ה קטן` instead of `הקטן`).
        const pos = charPos;
        const suffixWords: string[] = [];
        for (let j = i + 1; j < segs.length && suffixWords.length < 3; j++) {
          if (segs[j].kind !== 'text') continue;
          const text = (segs[j] as { kind: 'text'; text: string }).text;
          const words = text.trim().split(/\s+/).filter(Boolean);
          if (words.length === 0) continue;
          if (suffixWords.length === 0) {
            const leadingWs = text.match(/^\s+/)?.[0] || '';
            if (leadingWs) words[0] = leadingWs + words[0];
          }
          suffixWords.push(...words);
        }
        ops.push({ kind: 'insert', text: seg.text, charPos: pos, suffixWords: suffixWords.slice(0, 3) });
        i++;
      }
    }

    return ops;
  }

  /**
   * Counts non-overlapping occurrences of searchText in fullText before charPos.
   * The count is used as a 0-based index into Range.search() results to select
   * the correct occurrence without relying on prefix/suffix word matching.
   */
  private static countOccurrencesBefore(fullText: string, searchText: string, charPos: number): number {
    const segment = fullText.substring(0, charPos).toLowerCase();
    const needle = searchText.toLowerCase();
    let count = 0;
    let pos = 0;
    while (pos < segment.length) {
      const idx = segment.indexOf(needle, pos);
      if (idx === -1) break;
      count++;
      pos = idx + 1;
    }
    return count;
  }

  /**
   * Revert paragraph by rejecting track changes from a submit apply. No fallback replace.
   *
   * Matching uses fingerprints captured at apply time (handles Word minute-precision dates)
   * plus a tight ±{@link SUBMIT_REVERT_TOLERANCE_MS} ms window around the apply span.
   */
  static async revertTrackedChangesInParagraph(
    paragraphIndex: number,
    revertContext: SubmitRevertContext | null | undefined,
    _beforeText: string,
    toleranceMs: number = SUBMIT_REVERT_TOLERANCE_MS,
  ): Promise<boolean> {
    try {
      const runResult = await Word.run(async (context) => {
        if (revertContext == null) {
          return false;
        }

        const body = context.document.body;
        body.load('paragraphs');
        await context.sync();

        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        const totalParagraphs = paragraphs.items.length;
        if (paragraphIndex < 0 || paragraphIndex >= totalParagraphs) {
          logError('[RedlineInserter] revert: paragraph index out of range', {
            paragraphIndex,
            totalParagraphs,
          });
          return false;
        }

        const targetParagraph = paragraphs.items[paragraphIndex];
        const liveInTable = await liveParagraphIsInTable(targetParagraph, context);
        const inTable = liveInTable || revertSnapshotIsTableCell(revertContext);

        // Deterministic restore: put the exact pre-apply paragraph back (including w:ins/w:del).
        // Table cells use a lifted <w:p> package — never flatten via insertText.
        const restoreViaSnapshot =
          revertContext.preferOriginalRestore &&
          (revertContext.originalParagraphOoxmlPackage ||
            revertContext.originalParagraphOoxml ||
            (!inTable &&
              revertContext.originalParagraphText != null &&
              revertContext.originalParagraphText.length > 0));
        if (restoreViaSnapshot) {
          return 'needs_text_replace' as const;
        }
        const matchToleranceMs = revertMatchToleranceMs(revertContext, toleranceMs);

        const fingerprintKeys = new Set<string>();
        for (const f of revertContext.addedFingerprints) {
          fingerprintKeys.add(trackedChangeFingerprintKey(f));
        }
        const noFingerprintGuidance = revertContext.addedFingerprints.length === 0;

        // Authors our submit/apply paths stamp; counterparty redlines (other authors) are preserved.
        const knownSubmitAuthors = new Set([
          'user',
          'user edit',
          'bank of changes',
          'past negotiations',
          'schedule',
        ]);

        const shouldRejectMeta = (
          tcDate: Date | null,
          tcType: string,
          tcAuthor: string,
        ): { reject: boolean; matchesFingerprint: boolean; inApplyWindow: boolean; authorIsOurs: boolean } => {
          const tcFp: TrackedChangeFingerprint = {
            dateIso: tcDate ? tcDate.toISOString() : '',
            type: tcType,
          };
          const matchesFingerprint = fingerprintKeys.has(trackedChangeFingerprintKey(tcFp));
          const inApplyWindow =
            tcDate != null &&
            isTrackedChangeInApplyWindow(tcDate.getTime(), revertContext, matchToleranceMs);
          const authorIsOurs = knownSubmitAuthors.has(tcAuthor.trim().toLowerCase());
          const reject =
            matchesFingerprint || inApplyWindow || (noFingerprintGuidance && authorIsOurs);
          return { reject, matchesFingerprint, inApplyWindow, authorIsOurs };
        };

        const trackedChanges = (targetParagraph as any).getTrackedChanges();
        trackedChanges.load('items');
        await context.sync();

        const items = trackedChanges.items || [];

        if (items.length === 0) {
          const originalText = revertContext?.originalParagraphText;
          if (!inTable && originalText != null && originalText.length > 0) {
            return 'needs_text_replace' as const;
          }
          return false;
        }

        try {
          for (const tc of items) {
            tc.load(['date', 'type', 'author']);
          }
          await context.sync();
        } catch {
          if (!inTable && noFingerprintGuidance) {
            return 'needs_text_replace' as const;
          }
          return false;
        }

        const toReject: any[] = [];
        for (let i = 0; i < items.length; i++) {
          const tc = items[i];
          let tcDate: Date | null = null;
          let tcType = '';
          let tcAuthor = '';
          try {
            tcDate = tc.date ? new Date(tc.date) : null;
            tcType = String(tc.type ?? '');
            tcAuthor = String(tc.author ?? '');
          } catch {
            // per-item metadata read failed; treat as no metadata for this item
          }
          const decision = shouldRejectMeta(tcDate, tcType, tcAuthor);
          if (decision.reject) toReject.push(tc);
        }

        if (toReject.length === 0) {
          if (!inTable && revertContext?.originalParagraphText != null && noFingerprintGuidance) {
            return 'needs_text_replace' as const;
          }
          return false;
        }

        for (const tc of toReject) {
          tc.reject();
        }
        await context.sync();

        try {
          targetParagraph.getRange('Whole').select();
          await context.sync();
        } catch {
          // selection is best-effort
        }

        return true;
      });

      if (runResult === 'needs_text_replace') {
        const originalText = revertContext?.originalParagraphText;
        if (originalText != null || revertContext?.originalParagraphOoxmlPackage) {
          return await RedlineInserter.revertViaTextReplacement(
            paragraphIndex,
            originalText ?? '',
            revertContext?.originalParagraphOoxml,
            revertContext?.originalParagraphOoxmlPackage,
            {
              inTable: revertSnapshotIsTableCell(revertContext),
            },
          );
        }

        return await RedlineInserter.blindRejectAllTrackedChangesInParagraph(paragraphIndex);
      }
      return runResult === true;
    } catch (error) {
      logError('[RedlineInserter] revert tracked changes failed', error);
      throw error;
    }
  }

  /**
   * Win32 undo path: restore a paragraph to its original text by temporarily disabling Track
   * Changes, calling `insertText(originalText, Replace)`, then re-enabling Track Changes.
   *
   * This is the ONLY reliable undo strategy on Word Win32 for tracked changes created by
   * `insertText`. On Win32, those tracked changes are NOT actionable via `tc.reject()` through
   * the JS API — `tc.reject()` + `context.sync()` always throws `ItemNotFound` regardless of
   * whether metadata was read, regardless of context freshness, and regardless of batching vs
   * per-item sync. All three strategies were tried and confirmed broken on Win32.
   *
   * The TC-off + insertText approach is safe because:
   *   1. TC is disabled only for the duration of the replacement (restored in `finally`)
   *   2. `originalParagraphText` was captured from the document before the submit `insertText`,
   *      so it's the authoritative pre-submit text
   *   3. The result is a clean paragraph with no TC marks, matching the pre-submit state
   */
  private static async revertViaTextReplacement(
    paragraphIndex: number,
    originalParagraphText: string,
    originalParagraphOoxml?: string | null,
    originalParagraphOoxmlPackage?: string | null,
    options?: { inTable?: boolean },
  ): Promise<boolean> {
    try {
      return await Word.run(async (context) => {
        const body = context.document.body;
        body.load('paragraphs');
        context.document.load('changeTrackingMode');
        await context.sync();

        const paragraphs = body.paragraphs;
        paragraphs.load('items');
        await context.sync();

        if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
          logError('[RedlineInserter] revert (text-replace): paragraph index out of range', {
            paragraphIndex,
            totalParagraphs: paragraphs.items.length,
          });
          return false;
        }

        const targetParagraph = paragraphs.items[paragraphIndex];
        const originalMode = (context.document as any).changeTrackingMode ?? 'TrackAll';
        const inTable =
          options?.inTable === true ||
          OoxmlPackageWrapper.containsTableMarkup(
            originalParagraphOoxmlPackage || originalParagraphOoxml || '',
          ) ||
          (await liveParagraphIsInTable(targetParagraph, context));

        (context.document as any).changeTrackingMode = 'Off';
        await context.sync();

        let insertSucceeded = false;
        try {
          const insertablePackage = OoxmlPackageWrapper.toInsertableParagraphPackage(
            originalParagraphOoxmlPackage,
            originalParagraphText,
          );

          if (insertablePackage) {
            const locations: Array<'Content' | 'Whole'> = inTable ? ['Whole', 'Content'] : ['Whole'];
            for (const location of locations) {
              if (insertSucceeded) break;
              try {
                const range = targetParagraph.getRange(location);
                range.insertOoxml(insertablePackage, Word.InsertLocation.replace);
                await context.sync();
                insertSucceeded = true;
                logQuickResponseStage('revert: insertOoxml original paragraph package', {
                  paragraphIndex,
                  inTable,
                  location,
                  packageLen: insertablePackage.length,
                });
              } catch {
                // try next location / fallback
              }
            }
          }

          if (!insertSucceeded && !inTable && originalParagraphOoxml) {
            try {
              const packageXml = OoxmlPackageWrapper.wrapParagraphInPackage(originalParagraphOoxml);
              const range = targetParagraph.getRange('Whole');
              range.insertOoxml(packageXml, Word.InsertLocation.replace);
              await context.sync();
              insertSucceeded = true;
            } catch {
              // fall through to insertText (body paragraphs only)
            }
          }

          if (!insertSucceeded && inTable) {
            logError('[RedlineInserter] revert: table cell undo refused insertText (would flatten TCs)', {
              paragraphIndex,
              hadPackage: Boolean(originalParagraphOoxmlPackage),
              insertableLen: insertablePackage.length,
            });
          } else if (!insertSucceeded) {
            const range = targetParagraph.getRange('Whole');
            range.insertText(originalParagraphText, Word.InsertLocation.replace);
            await context.sync();
            insertSucceeded = true;
          }
        } finally {
          try {
            (context.document as any).changeTrackingMode = originalMode;
            await context.sync();
          } catch (restoreErr) {
            logError('[RedlineInserter] revert (text-replace): failed to restore TC mode', {
              error: restoreErr instanceof Error ? restoreErr.message : String(restoreErr),
              originalMode,
            });
          }
        }

        if (!insertSucceeded) return false;

        try {
          targetParagraph.getRange('Whole').select();
          await context.sync();
        } catch {
          // selection is best-effort
        }

        return true;
      });
    } catch (error) {
      logError('[RedlineInserter] revert (text-replace): failed', {
        paragraphIndex,
        error: error instanceof Error ? error.message : String(error),
        errorName: error instanceof Error ? error.name : typeof error,
      });
      throw error;
    }
  }

  /**
   * @deprecated Last-resort fallback for old revert contexts that predate the
   * `originalParagraphText` field. On Win32, `tc.reject()` is known to fail with `ItemNotFound`
   * for tracked changes created programmatically via `insertText`. This method will succeed on
   * Mac but WILL FAIL on Win32. Use {@link revertViaTextReplacement} instead.
   */
  private static async blindRejectAllTrackedChangesInParagraph(
    paragraphIndex: number,
  ): Promise<boolean> {
    return await Word.run(async (context) => {
      const body = context.document.body;
      body.load('paragraphs');
      await context.sync();

      const paragraphs = body.paragraphs;
      paragraphs.load('items');
      await context.sync();

      if (paragraphIndex < 0 || paragraphIndex >= paragraphs.items.length) {
        logError('[RedlineInserter] revert (blind): paragraph index out of range', {
          paragraphIndex,
          totalParagraphs: paragraphs.items.length,
        });
        return false;
      }

      const targetParagraph = paragraphs.items[paragraphIndex];
      const trackedChanges = (targetParagraph as any).getTrackedChanges();
      trackedChanges.load('items');
      await context.sync();

      const items = trackedChanges.items || [];
      if (items.length === 0) {
        return false;
      }

      for (const tc of items) {
        tc.reject();
      }
      await context.sync();

      try {
        targetParagraph.getRange('Whole').select();
        await context.sync();
      } catch {
        // selection is best-effort
      }

      return true;
    });
  }

  /**
   * Accept all tracked changes in the live Word paragraph at {@link paragraphIndex}.
   * Uses a single Word.run, resolves only the target paragraph (no full items load),
   * selects it to scroll into view, then accepts via native APIs or insertOoxml.
   */
  static async acceptTrackedChangesInParagraph(
    paragraphIndex: number,
    opts?: { uniqueLocalId?: string | null },
  ): Promise<{
    acceptedCount: number;
    paragraphOoxml: string | null;
    revertContext?: SubmitRevertContext;
  }> {
    try {
      return await Word.run(async (context) => {
        const targetParagraph = DocumentReader.queueTargetParagraph(
          context,
          paragraphIndex,
          opts?.uniqueLocalId,
        );
        if (!targetParagraph) {
          logError('[RedlineInserter] accept: paragraph not found', { paragraphIndex });
          return { acceptedCount: 0, paragraphOoxml: null };
        }

        const range = targetParagraph.getRange('Whole');
        // Do not range.select() — steals focus from the task pane and can block scrolling.

        targetParagraph.load('text');
        const trackedChanges = targetParagraph.getTrackedChanges();
        trackedChanges.load('items');
        const ooxmlResult = targetParagraph.getOoxml();
        await context.sync();

        if (targetParagraph.isNullObject) {
          logError('[RedlineInserter] accept: paragraph not found', { paragraphIndex });
          return { acceptedCount: 0, paragraphOoxml: null };
        }

        const livePackageXml =
          typeof ooxmlResult?.value === 'string' ? ooxmlResult.value : undefined;
        let paragraphOoxml = DocumentReader.paragraphXmlFromGetOoxmlResult(ooxmlResult);
        const initialHasMarkup = Boolean(
          paragraphOoxml && TrackChangesParser.hasTrackChangesMarkup(paragraphOoxml),
        );
        const initialTagCounts = paragraphOoxml
          ? TrackChangesParser.countWordTrackChangeTags(paragraphOoxml)
          : { ins: 0, del: 0 };

        // Capture pre-accept paragraph state for undo. Accept removes counterparty track changes;
        // undo must restore the original pkg:package (or single <w:p>) via insertOoxml — the same
        // path used for submit-as-is / submit-to-response undo on Win32.
        const originalParagraphText: string = targetParagraph.text ?? '';
        let originalParagraphOoxml: string | null = null;
        let originalParagraphOoxmlPackage: string | null = null;
        if (livePackageXml) {
          const prunedPackage = OoxmlPackageWrapper.keepOnlyTargetParagraphInPackage(
            livePackageXml,
            originalParagraphText,
          );
          if (prunedPackage && prunedPackage.length > 0) {
            originalParagraphOoxmlPackage = prunedPackage;
          }
        }
        originalParagraphOoxml = DocumentReader.singleParagraphXmlFromGetOoxmlResult(
          ooxmlResult,
          originalParagraphText,
        );
        log('[RedlineInserter] accept: captured pre-accept paragraph for undo', {
          paragraphIndex,
          originalTextLen: originalParagraphText.length,
          hasOriginalOoxml: originalParagraphOoxml != null,
          hasOriginalOoxmlPackage: originalParagraphOoxmlPackage != null,
          initialInsTags: initialTagCounts.ins,
          initialDelTags: initialTagCounts.del,
        });

        const applyStartedAtMs = Date.now();
        let nativeAcceptedCount = 0;
        try {
          const items = trackedChanges.items ?? [];
          if (items.length > 0) {
            for (const tc of items) {
              tc.accept();
            }
            await context.sync();
            nativeAcceptedCount = items.length;
            log('[RedlineInserter] accept: accepted via getTrackedChanges().accept()', {
              count: nativeAcceptedCount,
              paragraphIndex,
              initialInsTags: initialTagCounts.ins,
              initialDelTags: initialTagCounts.del,
            });
          }
        } catch (tcErr) {
          logWarn('[RedlineInserter] accept: getTrackedChanges failed', {
            paragraphIndex,
            error: tcErr instanceof Error ? tcErr.message : String(tcErr),
          });
        }

        // Re-fetch OOXML after native accept. On Win32, getTrackedChanges() often exposes only a
        // subset of revisions (e.g. 1 object) while the paragraph OOXML still contains multiple
        // w:ins/w:del blocks (split insertions, format revisions in w:rPr, etc.). The OOXML
        // insertOoxml path must run whenever markup remains — not only when native count is 0.
        const afterNativeOoxmlResult = targetParagraph.getOoxml();
        await context.sync();
        let afterNativePackageXml =
          typeof afterNativeOoxmlResult?.value === 'string'
            ? afterNativeOoxmlResult.value
            : livePackageXml;
        let afterNativeParagraphOoxml =
          DocumentReader.paragraphXmlFromGetOoxmlResult(afterNativeOoxmlResult) ?? paragraphOoxml;
        const remainingTagCounts = afterNativeParagraphOoxml
          ? TrackChangesParser.countWordTrackChangeTags(afterNativeParagraphOoxml)
          : { ins: 0, del: 0 };
        const stillHasMarkup = Boolean(
          afterNativeParagraphOoxml &&
            TrackChangesParser.hasTrackChangesMarkup(afterNativeParagraphOoxml),
        );

        let ooxmlAcceptedCount = 0;
        if (stillHasMarkup && afterNativeParagraphOoxml) {
          if (nativeAcceptedCount > 0) {
            logWarn(
              '[RedlineInserter] accept: native accept incomplete — remaining OOXML markup, using insertOoxml fallback',
              {
                paragraphIndex,
                nativeAcceptedCount,
                remainingInsTags: remainingTagCounts.ins,
                remainingDelTags: remainingTagCounts.del,
                remainingSnippet: TrackChangesParser.firstWordTrackChangeSnippet(
                  afterNativeParagraphOoxml,
                ),
                diagnosis:
                  'Win32 getTrackedChanges() often returns fewer revision objects than w:ins/w:del ' +
                  'tags in paragraph OOXML. Accepting via insertOoxml when markup remains.',
              },
            );
          }
          const ooxmlAcceptResult = await RedlineInserter.acceptPhantomMarkupViaInsertOoxml(
            range,
            context.document,
            context,
            paragraphIndex,
            afterNativeParagraphOoxml,
            afterNativePackageXml,
          );
          ooxmlAcceptedCount = ooxmlAcceptResult.acceptedCount;
        }

        const finalOoxmlResult = targetParagraph.getOoxml();
        await context.sync();
        paragraphOoxml =
          DocumentReader.paragraphXmlFromGetOoxmlResult(finalOoxmlResult) ??
          afterNativeParagraphOoxml ??
          paragraphOoxml;

        const finalHasMarkup = Boolean(
          paragraphOoxml && TrackChangesParser.hasTrackChangesMarkup(paragraphOoxml),
        );
        let acceptedCount = 0;
        if (initialHasMarkup && !finalHasMarkup) {
          acceptedCount = Math.max(nativeAcceptedCount, ooxmlAcceptedCount, 1);
        } else if (!initialHasMarkup) {
          acceptedCount = nativeAcceptedCount;
        } else {
          acceptedCount = nativeAcceptedCount + ooxmlAcceptedCount;
        }

        if (acceptedCount === 0) {
          log('[RedlineInserter] accept: no tracked changes found in paragraph', {
            paragraphIndex,
            initialHasMarkup,
            finalHasMarkup,
          });
        } else if (finalHasMarkup) {
          logWarn('[RedlineInserter] accept: finished but OOXML still has track-change markup', {
            paragraphIndex,
            nativeAcceptedCount,
            ooxmlAcceptedCount,
            remainingInsTags: paragraphOoxml
              ? TrackChangesParser.countWordTrackChangeTags(paragraphOoxml).ins
              : 0,
            remainingDelTags: paragraphOoxml
              ? TrackChangesParser.countWordTrackChangeTags(paragraphOoxml).del
              : 0,
            remainingSnippet: paragraphOoxml
              ? TrackChangesParser.firstWordTrackChangeSnippet(paragraphOoxml)
              : '',
          });
        } else {
          log('[RedlineInserter] accept: done', {
            paragraphIndex,
            nativeAcceptedCount,
            ooxmlAcceptedCount,
            acceptedCount,
            viaOoxmlFallback: ooxmlAcceptedCount > 0,
          });
        }

        const applyFinishedAtMs = Date.now();
        let revertContext: SubmitRevertContext | undefined;
        if (
          acceptedCount > 0 &&
          (originalParagraphText.length > 0 ||
            originalParagraphOoxml != null ||
            originalParagraphOoxmlPackage != null)
        ) {
          revertContext = {
            applyStartedAtMs,
            applyFinishedAtMs,
            // Accept does not add our tracked changes — undo restores the pre-accept paragraph
            // via originalParagraphOoxmlPackage / originalParagraphText (OOXML round-trip).
            addedFingerprints: [],
            paragraphIndex,
            originalParagraphText,
            ...(originalParagraphOoxml != null ? { originalParagraphOoxml } : {}),
            ...(originalParagraphOoxmlPackage != null
              ? { originalParagraphOoxmlPackage }
              : {}),
          };
        }

        return { acceptedCount, paragraphOoxml, revertContext };
      });
    } catch (error) {
      logError('[RedlineInserter] accept tracked changes failed', {
        paragraphIndex,
        error,
      });
      throw error;
    }
  }

  /**
   * Accept phantom OOXML markup: accept w:ins/w:del in live OOXML, replace via insertOoxml.
   */
  private static async acceptPhantomMarkupViaInsertOoxml(
    range: Word.Range,
    _doc: Word.Document,
    context: Word.RequestContext,
    paragraphIndex: number,
    liveOoxml: string,
    livePackageXml?: string,
  ): Promise<{ acceptedCount: number }> {
    try {
      if (!TrackChangesParser.hasTrackChangesMarkup(liveOoxml)) {
        return { acceptedCount: 0 };
      }

      const acceptedParagraphXml = TrackChangesParser.acceptAllTrackChangesInOoxml(liveOoxml);
      if (
        !acceptedParagraphXml ||
        TrackChangesParser.hasWordTrackChangesMarkup(acceptedParagraphXml)
      ) {
        logWarn('[RedlineInserter] accept: OOXML accept transform incomplete', { paragraphIndex });
        return { acceptedCount: 0 };
      }

      // Prefer patching the live package returned by getOoxml() — it already has all
      // namespaces, relationship parts, and content types that Word requires for insertOoxml.
      // Applying the accept transform to the full package XML avoids GeneralException caused
      // by missing relationship references (REF fields, cross-references) in a synthetic package.
      let packageXml: string;
      if (livePackageXml?.trim() && TrackChangesParser.hasTrackChangesMarkup(livePackageXml)) {
        packageXml = TrackChangesParser.acceptAllTrackChangesInOoxml(livePackageXml);
      } else if (livePackageXml?.trim()) {
        packageXml = livePackageXml;
      } else {
        packageXml = OoxmlPackageWrapper.wrapParagraphInPackage(acceptedParagraphXml);
      }
      if (!packageXml) {
        logWarn('[RedlineInserter] accept: OOXML package wrap failed', { paragraphIndex });
        return { acceptedCount: 0 };
      }

      const rangeWithOoxml = range as Word.Range & {
        insertOoxml?: (xml: string, loc: Word.InsertLocation) => void;
      };
      if (typeof rangeWithOoxml.insertOoxml !== 'function') {
        logWarn('[RedlineInserter] accept: insertOoxml not supported', { paragraphIndex });
        return { acceptedCount: 0 };
      }

      // Turn off track changes for insert only — otherwise Word wraps the replacement as new revisions.
      const doc = context.document as Word.Document & { changeTrackingMode?: string };
      doc.load('changeTrackingMode');
      await context.sync();
      const previousTrackingMode = String((doc as any).changeTrackingMode ?? '');
      const shouldRestoreTracking =
        previousTrackingMode === 'TrackAll' || previousTrackingMode === 'TrackMineOnly';

      if (shouldRestoreTracking) {
        (doc as any).changeTrackingMode = 'Off';
        await context.sync();
      }

      try {
        rangeWithOoxml.insertOoxml(packageXml, Word.InsertLocation.replace);
        await context.sync();
      } catch (insertErr) {
        logWarn('[RedlineInserter] accept: insertOoxml failed', {
          paragraphIndex,
          error: insertErr instanceof Error ? insertErr.message : String(insertErr),
        });
        return { acceptedCount: 0 };
      } finally {
        if (shouldRestoreTracking) {
          try {
            (doc as any).changeTrackingMode = previousTrackingMode;
            await context.sync();
          } catch (restoreErr) {
            logWarn('[RedlineInserter] accept: failed to restore changeTrackingMode', {
              paragraphIndex,
              previousTrackingMode,
              error: restoreErr instanceof Error ? restoreErr.message : String(restoreErr),
            });
          }
        }
      }

      const markupCount = (liveOoxml.match(/<w:(?:ins|del)\b/gi) || []).length;
      log('[RedlineInserter] accept: applied accepted OOXML via insertOoxml', {
        paragraphIndex,
        markupElements: markupCount,
      });
      return { acceptedCount: Math.max(markupCount, 1) };
    } catch (err) {
      logWarn('[RedlineInserter] accept: insertOoxml path failed', {
        paragraphIndex,
        error: err instanceof Error ? err.message : String(err),
      });
      return { acceptedCount: 0 };
    }
  }
}
